/**
 * Every word the driver reads, in every language ROARDRIVE speaks.
 *
 * English (US) is the master: the other dictionaries must answer every one of its keys, which TypeScript
 * enforces below. Spanish is Spain's Spanish, never Latin American (Marc's call).
 *
 * NOT here on purpose: the dash / simulator display (RaceDash), which stays in the language of a real
 * race display, and the legal documents, which are contracts and stay in their filed language.
 */
export type Lang = 'en' | 'es' | 'ca' | 'fr' | 'de' | 'it' | 'pt';

export const LANGS: { code: Lang; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'ca', name: 'Català' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Português' },
];

const en = {
  // ---- shared ----
  menu: 'Menu',
  account: 'Account',
  signIn: 'Sign in',
  logOut: 'Log out',
  language: 'Language',
  languageAuto: 'Automatic',
  languageAutoNote: 'Follows your phone: {name}',
  admin: 'Admin',
  adminPanel: 'Admin panel',
  diagnostics: 'Diagnostics',
  change: 'change',

  // ---- home ----
  tagline: 'Immersive engines for your car',
  nextEngine: 'Next engine',
  comingSoon: 'Coming soon',
  comingSoonBody: 'More engines are on the dyno. Sign in to hear about them first.',
  comingUp: 'COMING UP',
  free: 'FREE',
  owned: 'OWNED',
  howItWorks: 'How it works',
  step1Title: 'Open the web or the app',
  step1Body: 'roardrive.com in your car’s browser, or the RoarDrive app on your phone',
  step2Title: 'Pick an engine',
  step2Body: 'real race-car sound',
  step3Title: 'Drive',
  step3Body: 'your speed does the rest',
  questions: 'Questions',
  q1: 'Does it work in any car?',
  a1: 'Yes. GPS is enough, hardware is optional.',
  q2: 'Is it a subscription?',
  a2: 'No. One-time per engine, free demo first.',
  q3: 'Tesla screen?',
  a3: 'Optimized for it: full-screen dash, self-updating.',
  q4: 'Is it distracting?',
  a4: 'No interaction while driving. Set up parked, then just drive.',
  wallLine: 'This wall is reserved for real drivers.',
  wallHow: 'Drove it? Yours goes first:',
  terms: 'Terms',
  privacy: 'Privacy',
  cookies: 'Cookies',
  gdpr: 'GDPR rights',
  legalNotice: 'Legal notice',
  drawerSignInLead: 'One email, no password.',
  drawerSignInBody: 'Your purchases and settings follow you into every car. Sign in here, or scan a QR with your phone.',
  drawerAccountBody: 'Your engines and settings follow this email into every car.',
  dangerZone: 'Danger zone',
  deleteAccount: 'Delete account',
  deleting: 'Deleting…',
  deleteFailed: 'Could not delete: this car is not linked to that account. Sign in here first, or email privacy@roardrive.com.',
  deleteConfirm: 'Delete your ROARDRIVE account?\n\nYour email, linked cars, settings and lap times are deleted. Engine purchases are unlinked and cannot be restored to another account. This cannot be undone.',
  deletedOk: 'Account deleted.',

  // ---- pit box ----
  pitBox: 'Pit box',
  drive: 'DRIVE',
  drivingSource: 'Driving source',
  drivingSourceWhy: 'The sound follows your real speed. The faster we can read it, the tighter it sits on your right foot. Pick as many as you own. The engine always uses the best one that is alive.',
  drivingOn: 'Driving on',
  proHw: 'Pro hardware',
  instant: 'Instant',
  proHwMeta: '25 Hz · no delay at all',
  proHwDesc: 'RaceBox or OBD-II.',
  proHwDescDemo: 'RaceBox or OBD-II. Comes with the engine.',
  fullEngine: 'Full engine',
  teslaBrowser: 'Tesla in-car browser',
  fast: 'Fast',
  teslaMeta: '10 Hz · the car’s own GPS',
  teslaDesc: 'Open {site} on the Tesla’s own screen.',
  teslaFine: 'Open it on the Tesla’s screen and this becomes the play button.',
  playOnScreen: '▶ Play it on the car screen',
  sentToScreen: '✓ Sent. It’s driving on the car screen.',
  commander: 'Enhauto Commander',
  experimental: 'Experimental feature',
  experimentalSoon: 'Experimental feature · coming soon',
  commanderLead: 'Another layer of immersion: the engine hears the car itself. Move the Tesla from {n} into {d} and it takes 1st gear, the {doors}, {lights} and {wipers} play along, and {launch} arms by itself when you hold the brake down fully for 5 seconds.',
  doors: 'doors',
  lights: 'lights',
  wipers: 'wipers',
  launchControl: 'launch control',
  cmStep1: 'S3XY app: Commander, {scan}. Once, then never again.',
  cmStep2: 'Phone Wi-Fi: join {ssid}, password {pass}. The Wi-Fi itself, not the S3XY app: that pairs over Bluetooth and RoarDrive reads the CAN over this network.',
  cmStep3: 'iPhone Settings, RoarDrive, {local} on. Denied once, iOS never asks again and blocks it silently.',
  cmNoReply: 'No reply yet.',
  cmNoReplyBody: 'One of the three above is off.',
  waitingApp: 'Waiting for the phone app',
  connecting: 'Connecting…',
  linkedWaiting: 'Linked, waiting for CAN',
  tapToConnect: 'Tap to connect',
  optional: 'Optional',
  answering: 'Answering',
  sim: 'SIM',
  simFree: 'Free',
  simMeta: 'no car needed · no GPS',
  simDesc: 'Drive the engine right here: hold THROTTLE and BRAKE on the screen.',
  simAdminName: 'SIM ADMIN',
  simAdminDesc: 'The simulator with the session log, the audio levels and the telemetry pills.',
  phoneGps: 'Phone GPS',
  backup: 'Backup',
  delay: 'Delay',
  phoneMeta: '{hz} · the phone’s own GPS',
  phoneDescBackup: 'Always on underneath as the backup. Whatever else drops out, this keeps the engine running.',
  phoneDescAlone: 'Any car. Zero setup.',
  detecting: 'detecting…',
  appRow: 'RoarDrive app on your phone',
  appRowBody: 'Open it, sign in {who} and pick {pro}.',
  appRowWithEmail: 'as {email}',
  appRowNoEmail: 'with your email',
  appRowSignInHere: 'Or {link} so both ends share the account.',
  appRowSignInLink: 'sign in on this screen',
  linked: '✓ Linked',
  obdRow: 'OBD-II dongle',
  obdStep1: 'Plug a Bluetooth LE ELM327 adapter into the OBD-II port (usually under the steering wheel).',
  obdStep2: 'Ignition on.',
  obdStep3: 'It is found by itself. It reads the car’s own speedometer, so there is no satellite delay at all, and its revs let the engine change gear exactly when you do.',
  // NOT every ELM327 works: the cheap ones are classic Bluetooth 3.0, which an iPhone cannot see at
  // all, and the Wi-Fi ones eat the phone’s connection. Marc: "no vaya a ser que se compren uno que
  // no sirve" — so the requirements sit one tap away from the OBD steps, before anyone pays.
  obdBuyTitle: 'Which adapter do I buy?',
  obdBuyBle: 'Bluetooth LE, also written BLE or Bluetooth 4.0. If the box says only “Bluetooth” it is the old 3.0 kind and the iPhone cannot see it at all.',
  obdBuyElm: 'ELM327 compatible, which is what nearly all of them are.',
  obdBuyWifi: 'Not the Wi-Fi kind: it takes over the phone’s connection and we do not read it.',
  obdBuyPrice: 'It is a 25 to 45 euro part. Paying more buys workshop diagnostics we never use.',
  obdBuyNames: 'Ones that fit: Veepeak OBDCheck BLE+, Vgate iCar Pro BLE 4.0, vLinker MC+. Watch the plus sign: a vLinker FD without it is Bluetooth 3.0 and never shows up on an iPhone.',
  obdBuyEv: 'Electric car? Many of them do not publish speed on the standard port. If yours is electric, the RaceBox is the safe buy.',
  // the gearbox the app MEASURES instead of guessing: speed / rpm from the OBD dongle is the gear
  // you are in, so the catalogue of "typical" ladders stops being the only answer
  learnTitle: 'Your gearbox, measured',
  learnTitleSim: 'Measured in the simulator',
  learnProgress: '{gears} gears · {min} min',
  learnHint: 'Plug in an OBD-II adapter and drive as you always do. The app reads your speed and your revs and works out your gearbox on its own: nothing to fill in, nothing to hold, no need to stretch a single gear.',
  learnHintSim: 'Drive the simulator for a couple of minutes and this fills in with the engine’s own gears. It is the same measurement the OBD adapter does in a real car.',
  learnKeepGoing: 'Keep driving: the middle gears need a few minutes of ordinary roads.',
  learnReady: 'Measured from your own driving, up to {rpm} rpm.',
  learnUse: 'Use these gears',
  learnForget: 'forget and start again',
  // once the box is known, the engine changes gear WHEN THE DRIVER DOES (Marc: option B —
  // copy the gear, not the revs, or a car cruising at 2000 rpm would mumble all drive)
  learnFollowing: '● Following your car: you are in {gear}, and so is the engine.',
  // under the DRIVE button, and that is the whole of it: DRIVE, safe. The clauses are in the terms.
  driveSafe: 'safe',
  // the engine refused to run because no recordings arrived: never sing through the synthesiser
  audioDeniedTitle: 'The engine could not load',
  audioDeniedBody: 'The recordings did not arrive, so nothing is playing. It is usually the day\'s free listens being used up, or a bad connection. Sign in if you own this engine, or try again.',
  audioDeniedRetry: 'Try again',
  gpsDeniedTitle: 'Location is blocked',
  gpsDeniedBody: 'The car\'s browser is not handing over its position, so the engine has nothing to follow. It usually means location was refused once for this site: in the browser menu, clear the browsing data, open roardrive.com again and allow location when asked. And remember it only comes alive on the move: parked, it idles.',
  gpsDeniedRetry: 'Try again',
  yes: 'Yes',
  no: 'No',
  learnFollowLabel: 'Change gear when I do',
  learnUseWhy: 'Saving them is for the drives without the adapter: the engine then changes where your car changes.',
  learnNotRacebox: 'Only an OBD-II adapter can do this. A RaceBox gives position and speed, it does not read the engine, so it cannot see your gears.',
  raceboxRow: 'RaceBox',
  rbStep1: 'Turn the RaceBox on.',
  rbStep2: 'Close the official RaceBox app (Bluetooth allows one connection).',
  rbStep3: 'It connects by itself{state}. Once paired, next time it is automatic.',
  stateSearching: ' (searching…)',
  stateFound: ' (found, connecting…)',
  stateSatellites: ' (waiting for satellites…)',
  live: '✓ Live',
  connectedIgnition: 'Connected, ignition on?',
  connectedSatellites: 'Connected · waiting for satellites…',
  searching: 'Searching…',
  foundConnecting: 'Found, connecting…',
  fuseLive: 'Live · {hz} msg/s. RaceBox and OBD-II fuse, connect both at once.',
  fuseFoot: 'They fuse. Connect several at once.',
  startingBridge: 'Starting the hardware bridge… (Bluetooth on)',
  openBridge: 'Open the bridge (older app build)',
  findWiper: 'Find the wiper bit.',
  findWiperBody: 'Alternate, waiting about 5 s each time: wipers OFF tap {off}, wipers ON tap {on}, then off, then on again. Two pairs is enough.',
  off: 'off',
  on: 'on',
  reset: 'reset',

  gearRatio: 'Gear ratio',
  gearRatioWhy: 'Your car changes gear where a race car wouldn’t. Tell us yours and the engine shifts with you.',
  // ---- garage bay: without coverage (the engine on this device) ----
  offline: 'Without coverage',
  offlineWhy: 'The engine is stored on this device, so it drives on GPS alone: tunnels, mountain roads, no signal at all. It downloads by itself.',
  offlineChecking: 'Checking…',
  offlineDownloading: 'Downloading the engine',
  offlineProgress: '{done} of {total} MB',
  offlineReady: 'Ready without coverage',
  offlineReadyMeta: '{mb} MB on this device · GPS only',
  offlineFailed: 'The download did not finish',
  offlineFailedMeta: 'Some files are still missing. Try again with better signal.',
  offlineRetry: 'Try again',
  offlineNoNet: 'No coverage right now',
  offlineNoNetMeta: 'It downloads by itself when you are back online.',
  realistic: 'Realistic',
  yourCarTab: 'Your car',
  realisticNote: 'Each engine shifts exactly like its real car: its own gearbox, whatever you drive.',
  topSpeedPerGear: 'top speed per gear · {unit}',
  speedAtLimiter: 'speed at the limiter per gear · {unit}',
  evLadderNote: 'the engine’s ladder, scaled · {unit}',
  ev: 'EV',
  ice: 'ICE',
  topSpeed: 'Top speed',
  topSpeedOf: '{speed} top speed',
  carLimit: '{speed} · {name} limit',
  brandPick: 'Brand…',
  myOwnGears: 'My own gears',
  yourGears: 'Your gears',
  editedSuffix: '{model}, edited',
  backToCar: 'Back to the {model}',
  addGear: '+ gear',
  removeGear: '− gear',
  tapToChange: 'Tap a speed to change it. Max per gear = the engine’s own: {limits} km/h',

  demoLine: 'Simulator free · on the road {min} min today, pit limiter {cap} km/h · ',
  demoLineWeb: 'On the road {min} min today, pit limiter {cap} km/h · ',
  demoLineOpen: 'Simulator free · open week: on the road {min} min today, no limiter · ',
  demoLineOpenWeb: 'Open week: on the road {min} min today, no limiter · ',
  demoUsed: 'Simulator free · the minutes on the road are used up today · ',
  demoBadge: 'DEMO · limiter {cap} km/h · max gear {gear} · {time}',
  demoBadgeOpen: 'DEMO · {time}',
  demoBadgeBuy: 'UNLOCK',
  demoGearHeld: 'Demo: the gearbox stops at gear {gear} (limiter {cap} km/h). Unlock this engine for every gear.',
  demoUsedWeb: 'The minutes on the road are used up today · ',
  unlockFor: 'unlock for {price}',
  lastSession: 'LAST SESSION',
  best: 'best',
  laps: '{n} laps',
  lap1: '1 lap',
  teslaDetected: '🚗 Tesla detected: {name}',
  recommendCarBrowser: 'We recommend driving on your car’s browser: open {site} there. Better GPS, and a {play} button appears here.',
  playThere: '“play it there”',
  carScreenNotLinked: 'Car screen not linked. Open {site} on the Tesla’s browser and a {play} button appears here by itself.',

  // ---- engine page (outside the dash) ----
  tapToEnter: 'TAP TO ENTER',
  loading: 'LOADING…',
  volumeNote: 'Keep the volume moderate the first time',
  teslaAudioNote: 'Tesla: browser audio replaces the current media source',
  drivingInput: 'Driving input',
  rotatePhone: 'Rotate your phone',
  hardwareGuide: 'Hardware setup guide →',
  units: 'Units',
  metric: 'Metric',
  imperial: 'Imperial',
  demoOver: 'DEMO OVER',
  unlockFull: 'Full engine · {price} one-time',
  payOnPhone: 'Secure checkout by Stripe · the engine unlocks on this screen the moment you pay',
  backToEngines: '← Back to the engines',

  // ---- buy / unlock ----
  demoFineLeft: 'Simulator free, no clock · on the road {min} min today, limiter {cap} km/h',
  demoFineLeftWeb: 'On the road {min} min today, limiter {cap} km/h',
  demoFineLeftOpen: 'Simulator free, no clock · open week: on the road {min} min today, no limiter',
  demoFineLeftOpenWeb: 'Open week: on the road {min} min today, no limiter',
  demoFineUsed: 'Simulator free, no clock · the {min} min on the road are used up today',
  demoFineUsedWeb: 'The {min} min on the road are used up today',
  buyInApp: 'Buy · {price}',
  buying: 'Opening the App Store…',
  buyCancelled: 'Purchase cancelled.',
  buyDone: 'Unlocked. Enjoy it.',
  // ---- restore (Apple: a non-consumable purchase must be restorable, and the button must be easy to find)
  purchases: 'Purchases',
  restorePurchases: 'Restore purchases',
  restoreNote: 'Changed phone, or reinstalled the app? Your engines come back here. Nothing is charged again: on an iPhone it asks Apple for what you already own, everywhere else it goes by the account you bought from.',
  restoring: 'Restoring…',
  restoreDone: '✓ Your purchase is back. Enjoy it.',
  restoreNone: 'Nothing new to restore on this device. If you paid with another email, sign in with it and the engine follows.',
  restoreFailed: 'Could not restore right now. Try again in a moment.',
  buyFailed: 'The purchase did not go through. Nothing has been charged.',
  pqrScan: 'Scan with your phone to pay · unlocks automatically',
  pqrPreparing: 'Preparing secure checkout…',
  pqrUnlocked: '✓ Unlocked',
  pqrNotConfigured: 'Payments are not set up yet.',
  pqrError: 'Checkout is unavailable right now. Try again later.',
  buyNeedAccount: 'First, your account',
  buyNeedAccountBody: 'The engine is saved to the account, not to this screen. Sign in with your email and it comes back in the car, on the phone and after any reinstall. One code, no password.',
  buyNeedAccountBtn: 'CREATE ACCOUNT · SIGN IN',
  payNow: 'PAY {price}',
  pqrPayNote: 'Secure checkout by Stripe · the receipt goes to {email}',
  storeNotAnswering: 'The App Store is not answering. Try again in a moment.',
  buyingPlay: 'Opening Google Play…',
  storeNotAnsweringPlay: 'Google Play is not answering. Try again in a moment.',
  notInAppYet: 'Coming to the app',
  notInAppYetFine: 'This engine is not on sale in the app yet. The demo is yours today.',
  linkTitle: 'Sign in to your Tesla',
  linkDone: '✓ Done',
  linkDoneBody: 'Your Tesla is signed in as {email}. Look at the car screen. You can close this.',
  linkHint: 'Just your email, no password. New here? That creates your account.',
  signInBtn: 'SIGN IN',
  errSendCode: 'We could not send the code. Check the email and try again.',
  errBadCode: 'That code is not right.',
  engineStopHint: 'Stop the car to switch the engine off',
  demoWord: 'DEMO',
  demoSub: 'free',
  demoFine: 'The full engine, capped at {cap} km/h · a few minutes a day',
  purchase: 'PURCHASE',
  purchaseFine: 'One-time · the full engine, no limits · yours in every car',
  oneTime: '{price} · one-time',
  savedToAccount: '✓ {email} · the car is saved to this account',
  unlockNote: 'Unlocks here the moment you pay. The engine belongs to your account: sign in with the same email in any car or on any phone and it is there.',

  // ---- account ----
  signInSignUp: 'SIGN IN · SIGN UP',
  notifyMe: 'Tell me when a new engine is out',
  wheelHintTitle: 'MANUAL GEARBOX',
  wheelHintBody: 'One press on the left wheel of the steering wheel. Which way it goes is the car’s decision, not yours:',
  wheelHintUp: 'Pulling · up a gear',
  wheelHintDown: 'Slowing · down a gear',
  wheelHintPaddles: 'The paddles on screen do the same.',
  wheelHintOk: 'GOT IT',
  wheelHintNever: 'Don’t show this again',
  termsLine: 'By continuing you accept the {terms} and the {privacy}.',
  termsWord: 'Terms',
  privacyWord: 'Privacy Policy',
  accountCreated: 'Account created',
  welcomeBack: 'Welcome back',
  followCreated: 'your cars & settings will follow this email everywhere',
  followBack: 'your cars & settings are back',
  scanPhone: 'Scan with your phone.',
  scanPhoneBody: 'Sign in or sign up there and this screen signs in by itself.',
  orTypeHere: 'or type it here',
  emailIsAccount: 'Your email {is} the account, no password. Already have one? Same box. First time? The code creates it.',
  is: 'is',
  continueBtn: 'CONTINUE',
  sending: 'SENDING…',
  weEmailed: 'We emailed a code to {email}.',
  verify: 'VERIFY',
  checking: 'CHECKING…',
  changeEmail: 'Change email',
};

export type Key = keyof typeof en;
type Dict = Record<Key, string>;

const es: Dict = {
  menu: 'Menú',
  account: 'Cuenta',
  signIn: 'Entrar',
  logOut: 'Salir',
  language: 'Idioma',
  languageAuto: 'Automático',
  languageAutoNote: 'Sigue a tu teléfono: {name}',
  admin: 'Admin',
  adminPanel: 'Panel de admin',
  diagnostics: 'Diagnóstico',
  change: 'cambiar',

  tagline: 'Motores inmersivos para tu coche',
  nextEngine: 'Próximo motor',
  comingSoon: 'Muy pronto',
  comingSoonBody: 'Hay más motores en el banco de pruebas. Entra y serás el primero en oírlos.',
  comingUp: 'EN CAMINO',
  free: 'GRATIS',
  owned: 'TUYO',
  howItWorks: 'Cómo funciona',
  step1Title: 'Abre la web o la app',
  step1Body: 'roardrive.com en el navegador de tu coche, o la app RoarDrive en tu móvil',
  step2Title: 'Elige un motor',
  step2Body: 'sonido real de coche de carreras',
  step3Title: 'Conduce',
  step3Body: 'tu velocidad hace el resto',
  questions: 'Preguntas',
  q1: '¿Funciona en cualquier coche?',
  a1: 'Sí. Con el GPS basta, el hardware es opcional.',
  q2: '¿Es una suscripción?',
  a2: 'No. Un pago único por motor, con demo gratis antes.',
  q3: '¿Pantalla del Tesla?',
  a3: 'Optimizado para ella: cuadro a pantalla completa que se actualiza solo.',
  q4: '¿Distrae?',
  a4: 'No se toca nada mientras conduces. Se configura parado y luego solo conduces.',
  wallLine: 'Este muro está reservado a conductores de verdad.',
  wallHow: '¿Lo has probado? El tuyo va primero:',
  terms: 'Términos',
  privacy: 'Privacidad',
  cookies: 'Cookies',
  gdpr: 'Derechos RGPD',
  legalNotice: 'Aviso legal',
  drawerSignInLead: 'Un correo, sin contraseña.',
  drawerSignInBody: 'Tus compras y ajustes te siguen a cualquier coche. Entra aquí o escanea un QR con el móvil.',
  drawerAccountBody: 'Tus motores y ajustes siguen a este correo en cualquier coche.',
  dangerZone: 'Zona de riesgo',
  deleteAccount: 'Eliminar cuenta',
  deleting: 'Eliminando…',
  deleteFailed: 'No se ha podido eliminar: este coche no está vinculado a esa cuenta. Entra aquí primero o escribe a privacy@roardrive.com.',
  deleteConfirm: '¿Eliminar tu cuenta de ROARDRIVE?\n\nSe borran tu correo, los coches vinculados, los ajustes y los tiempos por vuelta. Los motores comprados se desvinculan y no se pueden pasar a otra cuenta. Esto no tiene vuelta atrás.',
  deletedOk: 'Cuenta eliminada.',

  pitBox: 'Box',
  drive: 'CONDUCIR',
  drivingSource: 'Fuente de velocidad',
  drivingSourceWhy: 'El sonido sigue tu velocidad real. Cuanto más rápido podamos leerla, más pegado va a tu pie derecho. Elige todas las que tengas: el motor usa siempre la mejor que esté viva.',
  drivingOn: 'Ahora mismo con',
  proHw: 'Hardware Pro',
  instant: 'Instantáneo',
  proHwMeta: '25 Hz · sin retardo alguno',
  proHwDesc: 'RaceBox u OBD-II.',
  proHwDescDemo: 'RaceBox u OBD-II. Viene con el motor.',
  fullEngine: 'Motor completo',
  teslaBrowser: 'Navegador del Tesla',
  fast: 'Rápido',
  teslaMeta: '10 Hz · el GPS del propio coche',
  teslaDesc: 'Abre {site} en la pantalla del propio Tesla.',
  teslaFine: 'Ábrelo en la pantalla del Tesla y esto se convierte en el botón de reproducir.',
  playOnScreen: '▶ Ponlo en la pantalla del coche',
  sentToScreen: '✓ Enviado. Está sonando en la pantalla del coche.',
  commander: 'Enhauto Commander',
  experimental: 'Función experimental',
  experimentalSoon: 'Función experimental · próximamente',
  commanderLead: 'Otra capa de inmersión: el motor escucha al propio coche. Pasa el Tesla de {n} a {d} y mete 1ª, las {doors}, las {lights} y el {wipers} entran en juego, y el {launch} se arma solo cuando pisas el freno a fondo 5 segundos.',
  doors: 'puertas',
  lights: 'luces',
  wipers: 'limpiaparabrisas',
  launchControl: 'launch control',
  cmStep1: 'App S3XY: Commander, {scan}. Una vez y nunca más.',
  cmStep2: 'Wi-Fi del móvil: conéctate a {ssid}, contraseña {pass}. El Wi-Fi en sí, no la app S3XY: esa se empareja por Bluetooth y RoarDrive lee el CAN por esta red.',
  cmStep3: 'Ajustes del iPhone, RoarDrive, {local} activado. Si lo deniegas una vez, iOS no vuelve a preguntar y lo bloquea en silencio.',
  cmNoReply: 'Aún no contesta.',
  cmNoReplyBody: 'Alguno de los tres pasos de arriba está sin hacer.',
  waitingApp: 'Esperando a la app del móvil',
  connecting: 'Conectando…',
  linkedWaiting: 'Enlazado, esperando el CAN',
  tapToConnect: 'Toca para conectar',
  optional: 'Opcional',
  answering: 'Respondiendo',
  sim: 'SIM',
  simFree: 'Gratis',
  simMeta: 'sin coche · sin GPS',
  simDesc: 'Conduce el motor aquí mismo: mantén pulsados THROTTLE y BRAKE en la pantalla.',
  simAdminName: 'SIM ADMIN',
  simAdminDesc: 'El simulador con el registro de sesión, los niveles de audio y las etiquetas de telemetría.',
  phoneGps: 'GPS del móvil',
  backup: 'Reserva',
  delay: 'Retardo',
  phoneMeta: '{hz} · el GPS del propio móvil',
  phoneDescBackup: 'Siempre encendido por debajo, como reserva. Caiga lo que caiga, esto mantiene el motor en marcha.',
  phoneDescAlone: 'Cualquier coche. Cero configuración.',
  detecting: 'midiendo…',
  appRow: 'App RoarDrive en tu móvil',
  appRowBody: 'Ábrela, entra {who} y elige {pro}.',
  appRowWithEmail: 'como {email}',
  appRowNoEmail: 'con tu correo',
  appRowSignInHere: 'O {link} para que ambos lados compartan la cuenta.',
  appRowSignInLink: 'entra en esta pantalla',
  linked: '✓ Enlazada',
  obdRow: 'Adaptador OBD-II',
  obdStep1: 'Enchufa un adaptador ELM327 con Bluetooth LE en el puerto OBD-II (suele estar bajo el volante).',
  obdStep2: 'Contacto dado.',
  obdStep3: 'Se encuentra solo. Lee el velocímetro del propio coche, así que no hay ningún retardo de satélite, y con sus revoluciones el motor cambia de marcha justo cuando cambias tú.',
  obdBuyTitle: '¿Qué adaptador compro?',
  obdBuyBle: 'Bluetooth LE, que también se escribe BLE o Bluetooth 4.0. Si la caja pone solo «Bluetooth» es del 3.0 antiguo y el iPhone ni lo ve.',
  obdBuyElm: 'Compatible con ELM327, que es lo que son casi todos.',
  obdBuyWifi: 'Los de Wi-Fi no: se quedan con la conexión del móvil y no los leemos.',
  obdBuyPrice: 'Es una pieza de 25 a 45 euros. Pagar más es pagar diagnóstico de taller que no usamos.',
  obdBuyNames: 'Valen estos: Veepeak OBDCheck BLE+, Vgate iCar Pro BLE 4.0, vLinker MC+. Ojo al signo más: el vLinker FD sin él es Bluetooth 3.0 y no aparece nunca en un iPhone.',
  obdBuyEv: '¿Coche eléctrico? Muchos no publican la velocidad en el puerto estándar. Si el tuyo es eléctrico, la compra segura es el RaceBox.',
  learnTitle: 'Tu caja de cambios, medida',
  learnTitleSim: 'Medida en el simulador',
  learnProgress: '{gears} marchas · {min} min',
  learnHint: 'Enchufa un adaptador OBD-II y conduce como siempre. La app lee tu velocidad y tus revoluciones y saca sola tu caja de cambios: no hay que rellenar nada, ni aguantar nada, ni estirar ninguna marcha.',
  learnHintSim: 'Conduce un par de minutos en el simulador y esto se rellena con las marchas del propio motor. Es la misma medición que hace el adaptador OBD en un coche real.',
  learnKeepGoing: 'Sigue conduciendo: las marchas de en medio necesitan unos minutos de carretera normal.',
  learnReady: 'Medido conduciendo tú, hasta {rpm} rpm.',
  learnUse: 'Usar estas marchas',
  learnForget: 'olvidar y empezar de nuevo',
  learnFollowing: '● Siguiendo a tu coche: vas en {gear}ª y el motor también.',
  driveSafe: 'de manera segura',
  audioDeniedTitle: 'El motor no ha podido cargar',
  audioDeniedBody: 'Las grabaciones no han llegado, así que no suena nada. Suele ser que se han agotado las escuchas gratuitas del día, o una mala conexión. Entra con tu cuenta si este motor es tuyo, o vuelve a intentarlo.',
  audioDeniedRetry: 'Reintentar',
  gpsDeniedTitle: 'La ubicación está bloqueada',
  gpsDeniedBody: 'El navegador del coche no entrega su posición, así que el motor no tiene nada que seguir. Suele ser que la ubicación se rechazó una vez para este sitio: en el menú del navegador, borra los datos de navegación, vuelve a abrir roardrive.com y acepta la ubicación cuando la pida. Y recuerda que solo cobra vida en marcha: parado, se queda al ralentí.',
  gpsDeniedRetry: 'Reintentar',
  yes: 'Sí',
  no: 'No',
  learnFollowLabel: 'Cambiar de marcha cuando yo cambie',
  learnUseWhy: 'Guardarlas es para los viajes sin el adaptador: el motor cambiará donde cambia tu coche.',
  learnNotRacebox: 'Esto solo lo puede hacer un adaptador OBD-II. El RaceBox da posición y velocidad, no lee el motor, así que no ve tus marchas.',
  raceboxRow: 'RaceBox',
  rbStep1: 'Enciende el RaceBox.',
  rbStep2: 'Cierra la app oficial de RaceBox (el Bluetooth solo admite una conexión).',
  rbStep3: 'Se conecta solo{state}. Una vez emparejado, la próxima vez es automático.',
  stateSearching: ' (buscando…)',
  stateFound: ' (encontrado, conectando…)',
  stateSatellites: ' (esperando satélites…)',
  live: '✓ En directo',
  connectedIgnition: 'Conectado, ¿contacto dado?',
  connectedSatellites: 'Conectado · esperando satélites…',
  searching: 'Buscando…',
  foundConnecting: 'Encontrado, conectando…',
  fuseLive: 'En directo · {hz} msg/s. RaceBox y OBD-II se fusionan, conecta los dos a la vez.',
  fuseFoot: 'Se fusionan. Conecta varios a la vez.',
  startingBridge: 'Arrancando el puente de hardware… (Bluetooth encendido)',
  openBridge: 'Abrir el puente (versión antigua de la app)',
  findWiper: 'Encuentra el bit del limpiaparabrisas.',
  findWiperBody: 'Alterna, esperando unos 5 s cada vez: limpia OFF pulsa {off}, limpia ON pulsa {on}, luego off y otra vez on. Con dos parejas basta.',
  off: 'off',
  on: 'on',
  reset: 'reiniciar',

  gearRatio: 'Desarrollo de marchas',
  gearRatioWhy: 'Tu coche cambia de marcha donde un coche de carreras no lo haría. Dinos el tuyo y el motor cambia contigo.',
  offline: 'Sin cobertura',
  offlineWhy: 'El motor se guarda en este dispositivo y funciona solo con el GPS: túneles, carreteras de montaña, sin ninguna señal. Se descarga solo.',
  offlineChecking: 'Comprobando…',
  offlineDownloading: 'Descargando el motor',
  offlineProgress: '{done} de {total} MB',
  offlineReady: 'Listo sin cobertura',
  offlineReadyMeta: '{mb} MB en este dispositivo · solo GPS',
  offlineFailed: 'La descarga no ha terminado',
  offlineFailedMeta: 'Faltan algunos archivos. Inténtalo otra vez con mejor señal.',
  offlineRetry: 'Reintentar',
  offlineNoNet: 'Ahora mismo no hay cobertura',
  offlineNoNetMeta: 'Se descarga solo cuando vuelvas a tener conexión.',
  realistic: 'Realista',
  yourCarTab: 'Tu coche',
  realisticNote: 'Cada motor cambia igual que su coche real: su propia caja, conduzcas lo que conduzcas.',
  topSpeedPerGear: 'velocidad máxima por marcha · {unit}',
  speedAtLimiter: 'velocidad en el limitador por marcha · {unit}',
  evLadderNote: 'la caja del motor, escalada · {unit}',
  ev: 'Eléctrico',
  ice: 'Gasolina',
  topSpeed: 'Velocidad máxima',
  topSpeedOf: '{speed} de máxima',
  carLimit: '{speed} · límite del {name}',
  brandPick: 'Marca…',
  myOwnGears: 'Mis propias marchas',
  yourGears: 'Tus marchas',
  editedSuffix: '{model}, editado',
  backToCar: 'Volver al {model}',
  addGear: '+ marcha',
  removeGear: '− marcha',
  tapToChange: 'Toca una velocidad para cambiarla. Máximo por marcha = el del propio motor: {limits} km/h',

  demoLine: 'Simulador gratis · en carretera {min} min hoy, limitador de box {cap} km/h · ',
  demoLineWeb: 'En carretera {min} min hoy, limitador de box {cap} km/h · ',
  demoLineOpen: 'Simulador gratis · semana abierta: en carretera {min} min hoy, sin limitador · ',
  demoLineOpenWeb: 'Semana abierta: en carretera {min} min hoy, sin limitador · ',
  demoUsed: 'Simulador gratis · los minutos en carretera se han agotado hoy · ',
  demoBadge: 'DEMO · limitador {cap} km/h · marcha máx. {gear} · {time}',
  demoBadgeOpen: 'DEMO · {time}',
  demoBadgeBuy: 'DESBLOQUEAR',
  demoGearHeld: 'Demo: la caja no pasa de {gear}ª (limitador {cap} km/h). Desbloquea este motor para tener todas las marchas.',
  demoUsedWeb: 'Los minutos en carretera se han agotado hoy · ',
  unlockFor: 'desbloquear por {price}',
  lastSession: 'ÚLTIMA SESIÓN',
  best: 'mejor',
  laps: '{n} vueltas',
  lap1: '1 vuelta',
  teslaDetected: '🚗 Tesla detectado: {name}',
  recommendCarBrowser: 'Te recomendamos conducir en el navegador de tu coche: abre {site} allí. Mejor GPS, y aquí aparece un botón de {play}.',
  playThere: '«ponlo allí»',
  carScreenNotLinked: 'Pantalla del coche sin enlazar. Abre {site} en el navegador del Tesla y aquí aparece solo un botón de {play}.',

  tapToEnter: 'TOCA PARA ENTRAR',
  loading: 'CARGANDO…',
  volumeNote: 'La primera vez, mantén el volumen moderado',
  teslaAudioNote: 'Tesla: el audio del navegador sustituye a la fuente que estés escuchando',
  drivingInput: 'Fuente de velocidad',
  rotatePhone: 'Gira el móvil',
  hardwareGuide: 'Guía de configuración del hardware →',
  units: 'Unidades',
  metric: 'Métrico',
  imperial: 'Imperial',
  demoOver: 'DEMO TERMINADA',
  unlockFull: 'Motor completo · {price} pago único',
  payOnPhone: 'Pago seguro con Stripe · el motor se desbloquea en esta pantalla en cuanto pagues',
  backToEngines: '← Volver a los motores',

  demoFineLeft: 'Simulador gratis, sin reloj · en carretera {min} min hoy, limitador {cap} km/h',
  demoFineLeftWeb: 'En carretera {min} min hoy, limitador {cap} km/h',
  demoFineLeftOpen: 'Simulador gratis, sin reloj · semana abierta: en carretera {min} min hoy, sin limitador',
  demoFineLeftOpenWeb: 'Semana abierta: en carretera {min} min hoy, sin limitador',
  demoFineUsed: 'Simulador gratis, sin reloj · los {min} min en carretera se han agotado hoy',
  demoFineUsedWeb: 'Los {min} min en carretera se han agotado hoy',
  buyInApp: 'Comprar · {price}',
  buying: 'Abriendo la App Store…',
  buyCancelled: 'Compra cancelada.',
  buyDone: 'Desbloqueado. Disfrútalo.',
  purchases: 'Compras',
  restorePurchases: 'Restaurar compras',
  restoreNote: '¿Has cambiado de móvil o has reinstalado la app? Aquí recuperas tus motores. No se cobra nada otra vez: en el iPhone se le pregunta a Apple qué es tuyo, y en todo lo demás se mira tu cuenta.',
  restoring: 'Restaurando…',
  restoreDone: '✓ Tu compra ha vuelto. Disfrútala.',
  restoreNone: 'No hay nada nuevo que restaurar en este dispositivo. Si pagaste con otro email, entra con él y el motor te sigue.',
  restoreFailed: 'No se ha podido restaurar ahora mismo. Inténtalo en un momento.',
  buyFailed: 'La compra no se ha completado. No se ha cobrado nada.',
  pqrScan: 'Escanea con el móvil para pagar · se desbloquea solo',
  pqrPreparing: 'Preparando el pago seguro…',
  pqrUnlocked: '✓ Desbloqueado',
  pqrNotConfigured: 'Los pagos aún no están configurados.',
  pqrError: 'El pago no está disponible ahora mismo. Inténtalo más tarde.',
  buyNeedAccount: 'Primero, tu cuenta',
  buyNeedAccountBody: 'El motor se guarda en la cuenta, no en esta pantalla. Entra con tu correo y lo recuperas en el coche, en el móvil y después de cualquier reinstalación. Un código, sin contraseña.',
  buyNeedAccountBtn: 'CREAR CUENTA · ENTRAR',
  payNow: 'PAGAR {price}',
  pqrPayNote: 'Pago seguro con Stripe · el recibo va a {email}',
  storeNotAnswering: 'La App Store no responde. Inténtalo en un momento.',
  buyingPlay: 'Abriendo Google Play…',
  storeNotAnsweringPlay: 'Google Play no responde. Inténtalo en un momento.',
  notInAppYet: 'Pronto en la app',
  notInAppYetFine: 'Este motor aún no está a la venta en la app. La demo ya es tuya.',
  linkTitle: 'Entra en tu Tesla',
  linkDone: '✓ Listo',
  linkDoneBody: 'Tu Tesla ha entrado como {email}. Mira la pantalla del coche. Ya puedes cerrar esto.',
  linkHint: 'Solo tu correo, sin contraseña. ¿Primera vez? Eso crea tu cuenta.',
  signInBtn: 'ENTRAR',
  errSendCode: 'No hemos podido enviar el código. Revisa el correo e inténtalo otra vez.',
  errBadCode: 'Ese código no es correcto.',
  engineStopHint: 'Para el coche para apagar el motor',
  demoWord: 'DEMO',
  demoSub: 'gratis',
  demoFine: 'El motor completo, limitado a {cap} km/h · unos minutos al día',
  purchase: 'COMPRAR',
  purchaseFine: 'Pago único · el motor completo, sin límites · tuyo en cualquier coche',
  oneTime: '{price} · pago único',
  savedToAccount: '✓ {email} · el coche queda guardado en esta cuenta',
  unlockNote: 'Se desbloquea aquí en cuanto pagues. El motor es de tu cuenta: entra con el mismo correo en cualquier coche o móvil y lo tienes.',

  signInSignUp: 'ENTRAR · CREAR CUENTA',
  notifyMe: 'Avísame cuando salga un motor nuevo',
  wheelHintTitle: 'CAMBIO MANUAL',
  wheelHintBody: 'Una pulsación en la rueda izquierda del volante. Hacia dónde va lo decide el coche, no tú:',
  wheelHintUp: 'Acelerando · sube de marcha',
  wheelHintDown: 'Frenando · baja de marcha',
  wheelHintPaddles: 'Las levas de la pantalla hacen lo mismo.',
  wheelHintOk: 'ENTENDIDO',
  wheelHintNever: 'No volver a mostrarlo',
  termsLine: 'Al continuar aceptas los {terms} y la {privacy}.',
  termsWord: 'Términos',
  privacyWord: 'Política de Privacidad',
  accountCreated: 'Cuenta creada',
  welcomeBack: 'Bienvenido de nuevo',
  followCreated: 'tus coches y ajustes seguirán a este correo a todas partes',
  followBack: 'tus coches y ajustes están de vuelta',
  scanPhone: 'Escanea con el móvil.',
  scanPhoneBody: 'Entra o crea la cuenta allí y esta pantalla entra sola.',
  orTypeHere: 'o escríbelo aquí',
  emailIsAccount: 'Tu correo {is} la cuenta, sin contraseña. ¿Ya tienes una? La misma casilla. ¿Primera vez? El código la crea.',
  is: 'es',
  continueBtn: 'CONTINUAR',
  sending: 'ENVIANDO…',
  weEmailed: 'Te hemos enviado un código a {email}.',
  verify: 'VERIFICAR',
  checking: 'COMPROBANDO…',
  changeEmail: 'Cambiar de correo',
};

const ca: Dict = {
  menu: 'Menú',
  account: 'Compte',
  signIn: 'Entrar',
  logOut: 'Sortir',
  language: 'Idioma',
  languageAuto: 'Automàtic',
  languageAutoNote: 'Segueix el teu telèfon: {name}',
  admin: 'Admin',
  adminPanel: 'Tauler d’admin',
  diagnostics: 'Diagnòstic',
  change: 'canviar',

  tagline: 'Motors immersius per al teu cotxe',
  nextEngine: 'Pròxim motor',
  comingSoon: 'Molt aviat',
  comingSoonBody: 'Hi ha més motors al banc de proves. Entra i seràs el primer a sentir-los.',
  comingUp: 'EN CAMÍ',
  free: 'GRATIS',
  owned: 'TEU',
  howItWorks: 'Com funciona',
  step1Title: 'Obre el web o l’app',
  step1Body: 'roardrive.com al navegador del cotxe, o l’app RoarDrive al mòbil',
  step2Title: 'Tria un motor',
  step2Body: 'so real de cotxe de curses',
  step3Title: 'Condueix',
  step3Body: 'la teva velocitat fa la resta',
  questions: 'Preguntes',
  q1: 'Funciona en qualsevol cotxe?',
  a1: 'Sí. Amb el GPS n’hi ha prou, el maquinari és opcional.',
  q2: 'És una subscripció?',
  a2: 'No. Un pagament únic per motor, amb demo gratuïta abans.',
  q3: 'Pantalla del Tesla?',
  a3: 'Optimitzat per a ella: quadre a pantalla completa que s’actualitza sol.',
  q4: 'Distreu?',
  a4: 'No es toca res mentre condueixes. Es configura aturat i després només condueixes.',
  wallLine: 'Aquest mur està reservat als conductors de veritat.',
  wallHow: 'L’has provat? El teu va primer:',
  terms: 'Termes',
  privacy: 'Privacitat',
  cookies: 'Galetes',
  gdpr: 'Drets RGPD',
  legalNotice: 'Avís legal',
  drawerSignInLead: 'Un correu, sense contrasenya.',
  drawerSignInBody: 'Les teves compres i ajustos et segueixen a qualsevol cotxe. Entra aquí o escaneja un QR amb el mòbil.',
  drawerAccountBody: 'Els teus motors i ajustos segueixen aquest correu a qualsevol cotxe.',
  dangerZone: 'Zona de risc',
  deleteAccount: 'Eliminar el compte',
  deleting: 'Eliminant…',
  deleteFailed: 'No s’ha pogut eliminar: aquest cotxe no està vinculat a aquest compte. Entra aquí primer o escriu a privacy@roardrive.com.',
  deleteConfirm: 'Vols eliminar el teu compte de ROARDRIVE?\n\nS’esborren el correu, els cotxes vinculats, els ajustos i els temps per volta. Els motors comprats es desvinculen i no es poden passar a un altre compte. Això no té marxa enrere.',
  deletedOk: 'Compte eliminat.',

  pitBox: 'Box',
  drive: 'CONDUIR',
  drivingSource: 'Font de velocitat',
  drivingSourceWhy: 'El so segueix la teva velocitat real. Com més de pressa la puguem llegir, més enganxada va al teu peu dret. Tria’n tantes com en tinguis: el motor sempre fa servir la millor que estigui viva.',
  drivingOn: 'Ara mateix amb',
  proHw: 'Maquinari Pro',
  instant: 'Instantani',
  proHwMeta: '25 Hz · sense cap retard',
  proHwDesc: 'RaceBox o OBD-II.',
  proHwDescDemo: 'RaceBox o OBD-II. Ve amb el motor.',
  fullEngine: 'Motor complet',
  teslaBrowser: 'Navegador del Tesla',
  fast: 'Ràpid',
  teslaMeta: '10 Hz · el GPS del mateix cotxe',
  teslaDesc: 'Obre {site} a la pantalla del mateix Tesla.',
  teslaFine: 'Obre’l a la pantalla del Tesla i això es converteix en el botó de reproduir.',
  playOnScreen: '▶ Posa’l a la pantalla del cotxe',
  sentToScreen: '✓ Enviat. Està sonant a la pantalla del cotxe.',
  commander: 'Enhauto Commander',
  experimental: 'Funció experimental',
  experimentalSoon: 'Funció experimental · aviat',
  commanderLead: 'Una altra capa d’immersió: el motor escolta el mateix cotxe. Passa el Tesla de {n} a {d} i posa 1a, les {doors}, els {lights} i l’{wipers} hi entren, i el {launch} s’arma sol quan trepitges el fre a fons 5 segons.',
  doors: 'portes',
  lights: 'llums',
  wipers: 'eixugaparabrisa',
  launchControl: 'launch control',
  cmStep1: 'App S3XY: Commander, {scan}. Un cop i mai més.',
  cmStep2: 'Wi-Fi del mòbil: connecta’t a {ssid}, contrasenya {pass}. El Wi-Fi en si, no l’app S3XY: aquella s’aparella per Bluetooth i RoarDrive llegeix el CAN per aquesta xarxa.',
  cmStep3: 'Ajustos de l’iPhone, RoarDrive, {local} activat. Si el deniegues un cop, iOS no torna a preguntar i el bloqueja en silenci.',
  cmNoReply: 'Encara no contesta.',
  cmNoReplyBody: 'Algun dels tres passos de dalt està sense fer.',
  waitingApp: 'Esperant l’app del mòbil',
  connecting: 'Connectant…',
  linkedWaiting: 'Enllaçat, esperant el CAN',
  tapToConnect: 'Toca per connectar',
  optional: 'Opcional',
  answering: 'Responent',
  sim: 'SIM',
  simFree: 'Gratis',
  simMeta: 'sense cotxe · sense GPS',
  simDesc: 'Condueix el motor aquí mateix: mantén premuts THROTTLE i BRAKE a la pantalla.',
  simAdminName: 'SIM ADMIN',
  simAdminDesc: 'El simulador amb el registre de sessió, els nivells d’àudio i les etiquetes de telemetria.',
  phoneGps: 'GPS del mòbil',
  backup: 'Reserva',
  delay: 'Retard',
  phoneMeta: '{hz} · el GPS del mateix mòbil',
  phoneDescBackup: 'Sempre encès per sota, com a reserva. Caigui el que caigui, això manté el motor en marxa.',
  phoneDescAlone: 'Qualsevol cotxe. Zero configuració.',
  detecting: 'mesurant…',
  appRow: 'App RoarDrive al teu mòbil',
  appRowBody: 'Obre-la, entra {who} i tria {pro}.',
  appRowWithEmail: 'com a {email}',
  appRowNoEmail: 'amb el teu correu',
  appRowSignInHere: 'O {link} perquè els dos costats comparteixin el compte.',
  appRowSignInLink: 'entra en aquesta pantalla',
  linked: '✓ Enllaçada',
  obdRow: 'Adaptador OBD-II',
  obdStep1: 'Endolla un adaptador ELM327 amb Bluetooth LE al port OBD-II (sol ser sota el volant).',
  obdStep2: 'Contacte donat.',
  obdStep3: 'Es troba sol. Llegeix el velocímetre del mateix cotxe, així que no hi ha cap retard de satèl·lit, i amb les seves revolucions el motor canvia de marxa just quan canvies tu.',
  obdBuyTitle: 'Quin adaptador compro?',
  obdBuyBle: 'Bluetooth LE, que també s’escriu BLE o Bluetooth 4.0. Si la capsa només posa «Bluetooth» és del 3.0 antic i l’iPhone ni el veu.',
  obdBuyElm: 'Compatible amb ELM327, que és el que són gairebé tots.',
  obdBuyWifi: 'Els de Wi-Fi no: es queden amb la connexió del mòbil i no els llegim.',
  obdBuyPrice: 'És una peça de 25 a 45 euros. Pagar més és pagar diagnosi de taller que no fem servir.',
  obdBuyNames: 'Aquests valen: Veepeak OBDCheck BLE+, Vgate iCar Pro BLE 4.0, vLinker MC+. Compte amb el signe més: el vLinker FD sense ell és Bluetooth 3.0 i no apareix mai en un iPhone.',
  obdBuyEv: 'Cotxe elèctric? Molts no publiquen la velocitat al port estàndard. Si el teu és elèctric, la compra segura és el RaceBox.',
  learnTitle: 'La teva caixa de canvis, mesurada',
  learnTitleSim: 'Mesurada al simulador',
  learnProgress: '{gears} marxes · {min} min',
  learnHint: 'Endolla un adaptador OBD-II i condueix com sempre. L’app llegeix la teva velocitat i les teves revolucions i treu sola la teva caixa de canvis: no has d’omplir res, ni aguantar res, ni estirar cap marxa.',
  learnHintSim: 'Condueix un parell de minuts al simulador i això s’omple amb les marxes del mateix motor. És la mateixa mesura que fa l’adaptador OBD en un cotxe real.',
  learnKeepGoing: 'Segueix conduint: les marxes del mig necessiten uns minuts de carretera normal.',
  learnReady: 'Mesurat conduint tu, fins a {rpm} rpm.',
  learnUse: 'Fer servir aquestes marxes',
  learnForget: 'oblidar i tornar a començar',
  learnFollowing: '● Seguint el teu cotxe: vas en {gear}a i el motor també.',
  driveSafe: 'de manera segura',
  audioDeniedTitle: 'El motor no ha pogut carregar',
  audioDeniedBody: 'Les gravacions no han arribat, així que no sona res. Sol ser que s\'han esgotat les escoltes gratuïtes del dia, o una mala connexió. Entra amb el teu compte si aquest motor és teu, o torna-ho a provar.',
  audioDeniedRetry: 'Tornar-ho a provar',
  gpsDeniedTitle: 'La ubicació està bloquejada',
  gpsDeniedBody: 'El navegador del cotxe no entrega la seva posició, així que el motor no té res a seguir. Sol ser que la ubicació es va rebutjar un cop per a aquest lloc: al menú del navegador, esborra les dades de navegació, torna a obrir roardrive.com i accepta la ubicació quan la demani. I recorda que només pren vida en marxa: aturat, es queda al ralentí.',
  gpsDeniedRetry: 'Tornar-ho a provar',
  yes: 'Sí',
  no: 'No',
  learnFollowLabel: 'Canviar de marxa quan jo canviï',
  learnUseWhy: 'Desar-les és per als viatges sense l’adaptador: el motor canviarà on canvia el teu cotxe.',
  learnNotRacebox: 'Això només ho pot fer un adaptador OBD-II. El RaceBox dona posició i velocitat, no llegeix el motor, així que no veu les teves marxes.',
  raceboxRow: 'RaceBox',
  rbStep1: 'Encén el RaceBox.',
  rbStep2: 'Tanca l’app oficial de RaceBox (el Bluetooth només admet una connexió).',
  rbStep3: 'Es connecta sol{state}. Un cop aparellat, la propera vegada és automàtic.',
  stateSearching: ' (cercant…)',
  stateFound: ' (trobat, connectant…)',
  stateSatellites: ' (esperant satèl·lits…)',
  live: '✓ En directe',
  connectedIgnition: 'Connectat, contacte donat?',
  connectedSatellites: 'Connectat · esperant satèl·lits…',
  searching: 'Cercant…',
  foundConnecting: 'Trobat, connectant…',
  fuseLive: 'En directe · {hz} msg/s. RaceBox i OBD-II es fusionen, connecta’ls tots dos alhora.',
  fuseFoot: 'Es fusionen. Connecta’n uns quants alhora.',
  startingBridge: 'Engegant el pont de maquinari… (Bluetooth encès)',
  openBridge: 'Obrir el pont (versió antiga de l’app)',
  findWiper: 'Troba el bit de l’eixugaparabrisa.',
  findWiperBody: 'Alterna, esperant uns 5 s cada cop: eixugaparabrisa OFF prem {off}, ON prem {on}, després off i un altre cop on. Amb dues parelles n’hi ha prou.',
  off: 'off',
  on: 'on',
  reset: 'reiniciar',

  gearRatio: 'Desenvolupament de marxes',
  gearRatioWhy: 'El teu cotxe canvia de marxa on un cotxe de curses no ho faria. Digues-nos el teu i el motor canvia amb tu.',
  offline: 'Sense cobertura',
  offlineWhy: 'El motor es guarda en aquest dispositiu i funciona només amb el GPS: túnels, carreteres de muntanya, sense cap senyal. Es descarrega sol.',
  offlineChecking: 'Comprovant…',
  offlineDownloading: 'Descarregant el motor',
  offlineProgress: '{done} de {total} MB',
  offlineReady: 'A punt sense cobertura',
  offlineReadyMeta: '{mb} MB en aquest dispositiu · només GPS',
  offlineFailed: 'La descàrrega no ha acabat',
  offlineFailedMeta: 'Falten alguns fitxers. Torna-ho a provar amb més senyal.',
  offlineRetry: 'Torna-ho a provar',
  offlineNoNet: 'Ara mateix no hi ha cobertura',
  offlineNoNetMeta: 'Es descarrega sol quan tornis a tenir connexió.',
  realistic: 'Realista',
  yourCarTab: 'El teu cotxe',
  realisticNote: 'Cada motor canvia igual que el seu cotxe real: la seva pròpia caixa, condueixis el que condueixis.',
  topSpeedPerGear: 'velocitat màxima per marxa · {unit}',
  speedAtLimiter: 'velocitat al limitador per marxa · {unit}',
  evLadderNote: 'la caixa del motor, escalada · {unit}',
  ev: 'Elèctric',
  ice: 'Gasolina',
  topSpeed: 'Velocitat màxima',
  topSpeedOf: '{speed} de màxima',
  carLimit: '{speed} · límit del {name}',
  brandPick: 'Marca…',
  myOwnGears: 'Les meves marxes',
  yourGears: 'Les teves marxes',
  editedSuffix: '{model}, editat',
  backToCar: 'Tornar al {model}',
  addGear: '+ marxa',
  removeGear: '− marxa',
  tapToChange: 'Toca una velocitat per canviar-la. Màxim per marxa = el del mateix motor: {limits} km/h',

  demoLine: 'Simulador gratis · a la carretera {min} min avui, limitador de box {cap} km/h · ',
  demoLineWeb: 'A la carretera {min} min avui, limitador de box {cap} km/h · ',
  demoLineOpen: 'Simulador gratis · setmana oberta: a la carretera {min} min avui, sense limitador · ',
  demoLineOpenWeb: 'Setmana oberta: a la carretera {min} min avui, sense limitador · ',
  demoUsed: 'Simulador gratis · els minuts a la carretera s’han esgotat avui · ',
  demoBadge: 'DEMO · limitador {cap} km/h · marxa màx. {gear} · {time}',
  demoBadgeOpen: 'DEMO · {time}',
  demoBadgeBuy: 'DESBLOQUEJAR',
  demoGearHeld: 'Demo: la caixa no passa de la {gear}a (limitador {cap} km/h). Desbloqueja aquest motor per tenir totes les marxes.',
  demoUsedWeb: 'Els minuts a la carretera s’han acabat avui · ',
  unlockFor: 'desbloqueja per {price}',
  lastSession: 'ÚLTIMA SESSIÓ',
  best: 'millor',
  laps: '{n} voltes',
  lap1: '1 volta',
  teslaDetected: '🚗 Tesla detectat: {name}',
  recommendCarBrowser: 'Et recomanem conduir al navegador del cotxe: obre {site} allà. Millor GPS, i aquí apareix un botó de {play}.',
  playThere: '«posa’l allà»',
  carScreenNotLinked: 'Pantalla del cotxe sense enllaçar. Obre {site} al navegador del Tesla i aquí apareix sol un botó de {play}.',

  tapToEnter: 'TOCA PER ENTRAR',
  loading: 'CARREGANT…',
  volumeNote: 'El primer cop, mantén el volum moderat',
  teslaAudioNote: 'Tesla: l’àudio del navegador substitueix la font que estiguis escoltant',
  drivingInput: 'Font de velocitat',
  rotatePhone: 'Gira el mòbil',
  hardwareGuide: 'Guia de configuració del maquinari →',
  units: 'Unitats',
  metric: 'Mètric',
  imperial: 'Imperial',
  demoOver: 'DEMO ACABADA',
  unlockFull: 'Motor complet · {price} pagament únic',
  payOnPhone: 'Pagament segur amb Stripe · el motor es desbloqueja en aquesta pantalla quan paguis',
  backToEngines: '← Tornar als motors',

  demoFineLeft: 'Simulador gratis, sense rellotge · a la carretera {min} min avui, limitador {cap} km/h',
  demoFineLeftWeb: 'A la carretera {min} min avui, limitador {cap} km/h',
  demoFineLeftOpen: 'Simulador gratis, sense rellotge · setmana oberta: a la carretera {min} min avui, sense limitador',
  demoFineLeftOpenWeb: 'Setmana oberta: a la carretera {min} min avui, sense limitador',
  demoFineUsed: 'Simulador gratis, sense rellotge · els {min} min a la carretera s’han esgotat avui',
  demoFineUsedWeb: 'Els {min} min a la carretera s’han acabat avui',
  buyInApp: 'Comprar · {price}',
  buying: 'Obrint l’App Store…',
  buyCancelled: 'Compra cancel·lada.',
  buyDone: 'Desbloquejat. Gaudeix-lo.',
  purchases: 'Compres',
  restorePurchases: 'Restaurar compres',
  restoreNote: 'Has canviat de mòbil o has reinstal·lat l’app? Aquí recuperes els teus motors. No es cobra res una altra vegada: a l’iPhone es pregunta a Apple què és teu, i a tota la resta es mira el teu compte.',
  restoring: 'Restaurant…',
  restoreDone: '✓ La teva compra ha tornat. Gaudeix-la.',
  restoreNone: 'No hi ha res nou a restaurar en aquest dispositiu. Si vas pagar amb un altre correu, entra amb aquell i el motor et segueix.',
  restoreFailed: 'Ara mateix no s’ha pogut restaurar. Torna-ho a provar en un moment.',
  buyFailed: 'La compra no s’ha completat. No s’ha cobrat res.',
  pqrScan: 'Escaneja amb el mòbil per pagar · es desbloqueja sol',
  pqrPreparing: 'Preparant el pagament segur…',
  pqrUnlocked: '✓ Desbloquejat',
  pqrNotConfigured: 'Els pagaments encara no estan configurats.',
  pqrError: 'El pagament no està disponible ara mateix. Prova-ho més tard.',
  buyNeedAccount: 'Primer, el teu compte',
  buyNeedAccountBody: 'El motor es desa al compte, no en aquesta pantalla. Entra amb el teu correu i el recuperes al cotxe, al mòbil i després de qualsevol reinstal·lació. Un codi, sense contrasenya.',
  buyNeedAccountBtn: 'CREAR COMPTE · ENTRAR',
  payNow: 'PAGAR {price}',
  pqrPayNote: 'Pagament segur amb Stripe · el rebut va a {email}',
  storeNotAnswering: 'L’App Store no respon. Prova-ho d’aquí a un moment.',
  buyingPlay: 'Obrint Google Play…',
  storeNotAnsweringPlay: 'Google Play no respon. Prova-ho d’aquí a un moment.',
  notInAppYet: 'Aviat a l’app',
  notInAppYetFine: 'Aquest motor encara no està a la venda a l’app. La demo ja és teva.',
  linkTitle: 'Entra al teu Tesla',
  linkDone: '✓ Fet',
  linkDoneBody: 'El teu Tesla ha entrat com a {email}. Mira la pantalla del cotxe. Ja pots tancar això.',
  linkHint: 'Només el teu correu, sense contrasenya. Primer cop? Això crea el teu compte.',
  signInBtn: 'ENTRAR',
  errSendCode: 'No hem pogut enviar el codi. Revisa el correu i torna-ho a provar.',
  errBadCode: 'Aquest codi no és correcte.',
  engineStopHint: 'Atura el cotxe per apagar el motor',
  demoWord: 'DEMO',
  demoSub: 'gratis',
  demoFine: 'El motor complet, limitat a {cap} km/h · uns minuts al dia',
  purchase: 'COMPRAR',
  purchaseFine: 'Pagament únic · el motor complet, sense límits · teu a qualsevol cotxe',
  oneTime: '{price} · pagament únic',
  savedToAccount: '✓ {email} · el cotxe queda desat en aquest compte',
  unlockNote: 'Es desbloqueja aquí quan paguis. El motor és del teu compte: entra amb el mateix correu en qualsevol cotxe o mòbil i el tens.',

  signInSignUp: 'ENTRAR · CREAR COMPTE',
  notifyMe: 'Avisa’m quan surti un motor nou',
  wheelHintTitle: 'CANVI MANUAL',
  wheelHintBody: 'Una premuda a la roda esquerra del volant. Cap on va ho decideix el cotxe, no tu:',
  wheelHintUp: 'Accelerant · puja de marxa',
  wheelHintDown: 'Frenant · baixa de marxa',
  wheelHintPaddles: 'Les lleves de la pantalla fan el mateix.',
  wheelHintOk: 'ENTESOS',
  wheelHintNever: 'No ho tornis a mostrar',
  termsLine: 'En continuar acceptes els {terms} i la {privacy}.',
  termsWord: 'Termes',
  privacyWord: 'Política de Privacitat',
  accountCreated: 'Compte creat',
  welcomeBack: 'Benvingut de nou',
  followCreated: 'els teus cotxes i ajustos seguiran aquest correu a tot arreu',
  followBack: 'els teus cotxes i ajustos han tornat',
  scanPhone: 'Escaneja amb el mòbil.',
  scanPhoneBody: 'Entra o crea el compte allà i aquesta pantalla entra sola.',
  orTypeHere: 'o escriu-lo aquí',
  emailIsAccount: 'El teu correu {is} el compte, sense contrasenya. Ja en tens un? La mateixa casella. Primer cop? El codi el crea.',
  is: 'és',
  continueBtn: 'CONTINUAR',
  sending: 'ENVIANT…',
  weEmailed: 'T’hem enviat un codi a {email}.',
  verify: 'VERIFICAR',
  checking: 'COMPROVANT…',
  changeEmail: 'Canviar de correu',
};

const fr: Dict = {
  menu: 'Menu',
  account: 'Compte',
  signIn: 'Se connecter',
  logOut: 'Se déconnecter',
  language: 'Langue',
  languageAuto: 'Automatique',
  languageAutoNote: 'Suit votre téléphone : {name}',
  admin: 'Admin',
  adminPanel: 'Panneau admin',
  diagnostics: 'Diagnostic',
  change: 'changer',

  tagline: 'Des moteurs immersifs pour votre voiture',
  nextEngine: 'Prochain moteur',
  comingSoon: 'Bientôt',
  comingSoonBody: 'D’autres moteurs sont au banc d’essai. Connectez-vous pour les entendre en premier.',
  comingUp: 'À VENIR',
  free: 'GRATUIT',
  owned: 'ACQUIS',
  howItWorks: 'Comment ça marche',
  step1Title: 'Ouvrez le site ou l’appli',
  step1Body: 'roardrive.com dans le navigateur de la voiture, ou l’appli RoarDrive sur votre téléphone',
  step2Title: 'Choisissez un moteur',
  step2Body: 'le vrai son d’une voiture de course',
  step3Title: 'Roulez',
  step3Body: 'votre vitesse fait le reste',
  questions: 'Questions',
  q1: 'Ça marche dans n’importe quelle voiture ?',
  a1: 'Oui. Le GPS suffit, le matériel est optionnel.',
  q2: 'C’est un abonnement ?',
  a2: 'Non. Un paiement unique par moteur, avec une démo gratuite avant.',
  q3: 'L’écran de la Tesla ?',
  a3: 'Optimisé pour lui : tableau de bord plein écran, mis à jour tout seul.',
  q4: 'Est-ce que ça distrait ?',
  a4: 'Aucune manipulation en roulant. On règle à l’arrêt, puis on conduit.',
  wallLine: 'Ce mur est réservé aux vrais conducteurs.',
  wallHow: 'Vous l’avez essayé ? Le vôtre passe en premier :',
  terms: 'Conditions',
  privacy: 'Confidentialité',
  cookies: 'Cookies',
  gdpr: 'Droits RGPD',
  legalNotice: 'Mentions légales',
  drawerSignInLead: 'Un e-mail, pas de mot de passe.',
  drawerSignInBody: 'Vos achats et réglages vous suivent dans chaque voiture. Connectez-vous ici, ou scannez un QR avec le téléphone.',
  drawerAccountBody: 'Vos moteurs et réglages suivent cet e-mail dans chaque voiture.',
  dangerZone: 'Zone sensible',
  deleteAccount: 'Supprimer le compte',
  deleting: 'Suppression…',
  deleteFailed: 'Suppression impossible : cette voiture n’est pas liée à ce compte. Connectez-vous ici d’abord, ou écrivez à privacy@roardrive.com.',
  deleteConfirm: 'Supprimer votre compte ROARDRIVE ?\n\nVotre e-mail, les voitures liées, les réglages et les temps au tour sont effacés. Les moteurs achetés sont dissociés et ne peuvent pas être transférés. C’est irréversible.',
  deletedOk: 'Compte supprimé.',

  pitBox: 'Stand',
  drive: 'ROULER',
  drivingSource: 'Source de vitesse',
  drivingSourceWhy: 'Le son suit votre vitesse réelle. Plus vite on peut la lire, plus il colle à votre pied droit. Choisissez tout ce que vous avez : le moteur prend toujours la meilleure source active.',
  drivingOn: 'En ce moment avec',
  proHw: 'Matériel Pro',
  instant: 'Instantané',
  proHwMeta: '25 Hz · aucun délai',
  proHwDesc: 'RaceBox ou OBD-II.',
  proHwDescDemo: 'RaceBox ou OBD-II. Fourni avec le moteur.',
  fullEngine: 'Moteur complet',
  teslaBrowser: 'Navigateur de la Tesla',
  fast: 'Rapide',
  teslaMeta: '10 Hz · le GPS de la voiture',
  teslaDesc: 'Ouvrez {site} sur l’écran de la Tesla.',
  teslaFine: 'Ouvrez-le sur l’écran de la Tesla et ceci devient le bouton de lecture.',
  playOnScreen: '▶ Lancez-le sur l’écran de la voiture',
  sentToScreen: '✓ Envoyé. Ça tourne sur l’écran de la voiture.',
  commander: 'Enhauto Commander',
  experimental: 'Fonction expérimentale',
  experimentalSoon: 'Fonction expérimentale · bientôt',
  commanderLead: 'Une couche d’immersion en plus : le moteur écoute la voiture elle-même. Passez la Tesla de {n} à {d} et il enclenche la 1re, les {doors}, les {lights} et les {wipers} jouent avec, et le {launch} s’arme tout seul quand vous gardez le frein à fond 5 secondes.',
  doors: 'portes',
  lights: 'phares',
  wipers: 'essuie-glaces',
  launchControl: 'launch control',
  cmStep1: 'Appli S3XY : Commander, {scan}. Une fois, puis plus jamais.',
  cmStep2: 'Wi-Fi du téléphone : rejoignez {ssid}, mot de passe {pass}. Le Wi-Fi lui-même, pas l’appli S3XY : celle-ci s’appaire en Bluetooth et RoarDrive lit le CAN sur ce réseau.',
  cmStep3: 'Réglages iPhone, RoarDrive, {local} activé. Refusé une fois, iOS ne redemande jamais et bloque en silence.',
  cmNoReply: 'Pas encore de réponse.',
  cmNoReplyBody: 'L’une des trois étapes ci-dessus n’est pas faite.',
  waitingApp: 'En attente de l’appli',
  connecting: 'Connexion…',
  linkedWaiting: 'Relié, en attente du CAN',
  tapToConnect: 'Touchez pour connecter',
  optional: 'Optionnel',
  answering: 'Répond',
  sim: 'SIM',
  simFree: 'Gratuit',
  simMeta: 'sans voiture · sans GPS',
  simDesc: 'Conduisez le moteur ici même : maintenez THROTTLE et BRAKE à l’écran.',
  simAdminName: 'SIM ADMIN',
  simAdminDesc: 'Le simulateur avec le journal de session, les niveaux audio et les pastilles de télémétrie.',
  phoneGps: 'GPS du téléphone',
  backup: 'Secours',
  delay: 'Délai',
  phoneMeta: '{hz} · le GPS du téléphone',
  phoneDescBackup: 'Toujours actif en dessous, en secours. Quoi qu’il arrive, ça garde le moteur en vie.',
  phoneDescAlone: 'N’importe quelle voiture. Zéro réglage.',
  detecting: 'mesure…',
  appRow: 'Appli RoarDrive sur votre téléphone',
  appRowBody: 'Ouvrez-la, connectez-vous {who} et choisissez {pro}.',
  appRowWithEmail: 'en tant que {email}',
  appRowNoEmail: 'avec votre e-mail',
  appRowSignInHere: 'Ou {link} pour que les deux côtés partagent le compte.',
  appRowSignInLink: 'connectez-vous sur cet écran',
  linked: '✓ Reliée',
  obdRow: 'Boîtier OBD-II',
  obdStep1: 'Branchez un boîtier ELM327 Bluetooth LE sur la prise OBD-II (souvent sous le volant).',
  obdStep2: 'Contact mis.',
  obdStep3: 'Il est trouvé tout seul. Il lit le compteur de la voiture : aucun délai satellite, et son régime fait changer le moteur de rapport au moment exact où vous changez.',
  obdBuyTitle: 'Quel boîtier acheter ?',
  obdBuyBle: 'Bluetooth LE, écrit aussi BLE ou Bluetooth 4.0. Si la boîte indique seulement « Bluetooth », c’est l’ancien 3.0 et l’iPhone ne le voit même pas.',
  obdBuyElm: 'Compatible ELM327, ce que sont presque tous.',
  obdBuyWifi: 'Pas les modèles Wi-Fi : ils accaparent la connexion du téléphone et nous ne les lisons pas.',
  obdBuyPrice: 'C’est une pièce à 25 ou 45 euros. Payer plus, c’est payer du diagnostic d’atelier dont nous ne faisons rien.',
  obdBuyNames: 'Ceux-ci conviennent : Veepeak OBDCheck BLE+, Vgate iCar Pro BLE 4.0, vLinker MC+. Attention au signe plus : le vLinker FD sans lui est en Bluetooth 3.0 et n’apparaît jamais sur un iPhone.',
  obdBuyEv: 'Voiture électrique ? Beaucoup ne publient pas la vitesse sur la prise standard. Si la vôtre est électrique, l’achat sûr est le RaceBox.',
  learnTitle: 'Votre boîte, mesurée',
  learnTitleSim: 'Mesurée dans le simulateur',
  learnProgress: '{gears} rapports · {min} min',
  learnHint: 'Branchez un boîtier OBD-II et roulez comme d’habitude. L’app lit votre vitesse et votre régime et trouve votre boîte toute seule : rien à saisir, rien à tenir, aucun rapport à tirer.',
  learnHintSim: 'Roulez deux minutes dans le simulateur et ceci se remplit avec les rapports du moteur lui-même. C’est la mesure que fait le boîtier OBD dans une vraie voiture.',
  learnKeepGoing: 'Continuez à rouler : les rapports du milieu demandent quelques minutes de route ordinaire.',
  learnReady: 'Mesuré sur votre conduite, jusqu’à {rpm} tr/min.',
  learnUse: 'Utiliser ces rapports',
  learnForget: 'oublier et recommencer',
  learnFollowing: '● On suit votre voiture : vous êtes en {gear}e, le moteur aussi.',
  driveSafe: 'prudemment',
  audioDeniedTitle: 'Le moteur n\'a pas pu charger',
  audioDeniedBody: 'Les enregistrements ne sont pas arrivés, donc rien ne joue. C\'est souvent les écoutes gratuites du jour épuisées, ou une mauvaise connexion. Connectez-vous si ce moteur est à vous, ou réessayez.',
  audioDeniedRetry: 'Réessayer',
  gpsDeniedTitle: 'La position est bloquée',
  gpsDeniedBody: 'Le navigateur de la voiture ne transmet pas sa position, le moteur n\'a donc rien à suivre. En général la position a été refusée une fois pour ce site : dans le menu du navigateur, effacez les données de navigation, rouvrez roardrive.com et acceptez la position quand elle est demandée. Et n\'oubliez pas qu\'il ne prend vie qu\'en roulant : à l\'arrêt, il reste au ralenti.',
  gpsDeniedRetry: 'Réessayer',
  yes: 'Oui',
  no: 'Non',
  learnFollowLabel: 'Changer de rapport quand je change',
  learnUseWhy: 'Les enregistrer sert aux trajets sans le boîtier : le moteur changera là où votre voiture change.',
  learnNotRacebox: 'Seul un boîtier OBD-II peut le faire. Un RaceBox donne la position et la vitesse, il ne lit pas le moteur, donc il ne voit pas vos rapports.',
  raceboxRow: 'RaceBox',
  rbStep1: 'Allumez le RaceBox.',
  rbStep2: 'Fermez l’appli officielle RaceBox (le Bluetooth n’accepte qu’une connexion).',
  rbStep3: 'Il se connecte tout seul{state}. Une fois appairé, c’est automatique.',
  stateSearching: ' (recherche…)',
  stateFound: ' (trouvé, connexion…)',
  stateSatellites: ' (attente des satellites…)',
  live: '✓ En direct',
  connectedIgnition: 'Connecté, contact mis ?',
  connectedSatellites: 'Connecté · attente des satellites…',
  searching: 'Recherche…',
  foundConnecting: 'Trouvé, connexion…',
  fuseLive: 'En direct · {hz} msg/s. RaceBox et OBD-II fusionnent, branchez les deux.',
  fuseFoot: 'Ils fusionnent. Branchez-en plusieurs à la fois.',
  startingBridge: 'Démarrage du pont matériel… (Bluetooth activé)',
  openBridge: 'Ouvrir le pont (ancienne version de l’appli)',
  findWiper: 'Trouvez le bit des essuie-glaces.',
  findWiperBody: 'Alternez, en attendant environ 5 s : essuie-glaces OFF touchez {off}, ON touchez {on}, puis off, puis on. Deux paires suffisent.',
  off: 'off',
  on: 'on',
  reset: 'réinitialiser',

  gearRatio: 'Étagement des rapports',
  gearRatioWhy: 'Votre voiture change de rapport là où une voiture de course ne le ferait pas. Dites-nous la vôtre et le moteur change avec vous.',
  offline: 'Sans réseau',
  offlineWhy: 'Le moteur est stocké sur cet appareil et roule avec le GPS seul : tunnels, routes de montagne, aucun signal. Il se télécharge tout seul.',
  offlineChecking: 'Vérification…',
  offlineDownloading: 'Téléchargement du moteur',
  offlineProgress: '{done} sur {total} Mo',
  offlineReady: 'Prêt sans réseau',
  offlineReadyMeta: '{mb} Mo sur cet appareil · GPS seul',
  offlineFailed: 'Le téléchargement n’est pas terminé',
  offlineFailedMeta: 'Il manque encore des fichiers. Réessayez avec un meilleur signal.',
  offlineRetry: 'Réessayer',
  offlineNoNet: 'Pas de réseau pour l’instant',
  offlineNoNetMeta: 'Il se télécharge tout seul dès que la connexion revient.',
  realistic: 'Réaliste',
  yourCarTab: 'Votre voiture',
  realisticNote: 'Chaque moteur passe les rapports comme sa vraie voiture : sa propre boîte, quoi que vous conduisiez.',
  topSpeedPerGear: 'vitesse maxi par rapport · {unit}',
  speedAtLimiter: 'vitesse au rupteur par rapport · {unit}',
  evLadderNote: 'la boîte du moteur, mise à l’échelle · {unit}',
  ev: 'Électrique',
  ice: 'Essence',
  topSpeed: 'Vitesse maxi',
  topSpeedOf: '{speed} en pointe',
  carLimit: '{speed} · limite de la {name}',
  brandPick: 'Marque…',
  myOwnGears: 'Mes propres rapports',
  yourGears: 'Vos rapports',
  editedSuffix: '{model}, modifié',
  backToCar: 'Revenir à la {model}',
  addGear: '+ rapport',
  removeGear: '− rapport',
  tapToChange: 'Touchez une vitesse pour la changer. Maxi par rapport = celui du moteur : {limits} km/h',

  demoLine: 'Simulateur gratuit · sur la route {min} min aujourd’hui, limiteur {cap} km/h · ',
  demoLineWeb: 'Sur la route {min} min aujourd’hui, limiteur {cap} km/h · ',
  demoLineOpen: 'Simulateur gratuit · semaine ouverte : sur la route {min} min aujourd’hui, sans limiteur · ',
  demoLineOpenWeb: 'Semaine ouverte : sur la route {min} min aujourd’hui, sans limiteur · ',
  demoUsed: 'Simulateur gratuit · les minutes sur la route sont épuisées aujourd’hui · ',
  demoBadge: 'DÉMO · limiteur {cap} km/h · rapport max. {gear} · {time}',
  demoBadgeOpen: 'DÉMO · {time}',
  demoBadgeBuy: 'DÉBLOQUER',
  demoGearHeld: 'Démo : la boîte s’arrête au rapport {gear} (limiteur {cap} km/h). Débloquez ce moteur pour tous les rapports.',
  demoUsedWeb: 'Les minutes sur la route sont épuisées aujourd’hui · ',
  unlockFor: 'débloquer pour {price}',
  lastSession: 'DERNIÈRE SESSION',
  best: 'meilleur',
  laps: '{n} tours',
  lap1: '1 tour',
  teslaDetected: '🚗 Tesla détectée : {name}',
  recommendCarBrowser: 'Nous conseillons de rouler avec le navigateur de la voiture : ouvrez-y {site}. Meilleur GPS, et un bouton {play} apparaît ici.',
  playThere: '« lancer là-bas »',
  carScreenNotLinked: 'Écran de la voiture non relié. Ouvrez {site} dans le navigateur de la Tesla et un bouton {play} apparaît ici tout seul.',

  tapToEnter: 'TOUCHEZ POUR ENTRER',
  loading: 'CHARGEMENT…',
  volumeNote: 'La première fois, gardez un volume modéré',
  teslaAudioNote: 'Tesla : l’audio du navigateur remplace la source en cours',
  drivingInput: 'Source de vitesse',
  rotatePhone: 'Tournez votre téléphone',
  hardwareGuide: 'Guide de configuration du matériel →',
  units: 'Unités',
  metric: 'Métrique',
  imperial: 'Impérial',
  demoOver: 'DÉMO TERMINÉE',
  unlockFull: 'Moteur complet · {price} paiement unique',
  payOnPhone: 'Paiement sécurisé par Stripe · le moteur se débloque sur cet écran dès le paiement',
  backToEngines: '← Retour aux moteurs',

  demoFineLeft: 'Simulateur gratuit, sans chrono · sur la route {min} min aujourd’hui, limiteur {cap} km/h',
  demoFineLeftWeb: 'Sur la route {min} min aujourd’hui, limiteur {cap} km/h',
  demoFineLeftOpen: 'Simulateur gratuit, sans chrono · semaine ouverte : sur la route {min} min aujourd’hui, sans limiteur',
  demoFineLeftOpenWeb: 'Semaine ouverte : sur la route {min} min aujourd’hui, sans limiteur',
  demoFineUsed: 'Simulateur gratuit, sans chrono · les {min} min sur la route sont épuisées aujourd’hui',
  demoFineUsedWeb: 'Les {min} min sur la route sont épuisées aujourd’hui',
  buyInApp: 'Acheter · {price}',
  buying: 'Ouverture de l’App Store…',
  buyCancelled: 'Achat annulé.',
  buyDone: 'Débloqué. Bonne route.',
  purchases: 'Achats',
  restorePurchases: 'Restaurer mes achats',
  restoreNote: 'Changement de téléphone ou réinstallation ? Vos moteurs reviennent ici. Rien n’est facturé deux fois : sur iPhone, Apple est interrogé sur ce que vous possédez, partout ailleurs on regarde votre compte.',
  restoring: 'Restauration…',
  restoreDone: '✓ Votre achat est de retour. Bonne route.',
  restoreNone: 'Rien de nouveau à restaurer sur cet appareil. Si vous avez payé avec une autre adresse, connectez-vous avec celle-ci et le moteur suit.',
  restoreFailed: 'Restauration impossible pour l’instant. Réessayez dans un moment.',
  buyFailed: 'L’achat n’a pas abouti. Rien n’a été débité.',
  pqrScan: 'Scannez avec le téléphone pour payer · déblocage automatique',
  pqrPreparing: 'Préparation du paiement sécurisé…',
  pqrUnlocked: '✓ Débloqué',
  pqrNotConfigured: 'Les paiements ne sont pas encore configurés.',
  pqrError: 'Le paiement est indisponible pour le moment. Réessayez plus tard.',
  buyNeedAccount: 'D’abord, votre compte',
  buyNeedAccountBody: 'Le moteur est enregistré sur le compte, pas sur cet écran. Connectez-vous avec votre e-mail et vous le retrouvez dans la voiture, sur le téléphone et après toute réinstallation. Un code, sans mot de passe.',
  buyNeedAccountBtn: 'CRÉER UN COMPTE · SE CONNECTER',
  payNow: 'PAYER {price}',
  pqrPayNote: 'Paiement sécurisé par Stripe · le reçu part vers {email}',
  storeNotAnswering: 'L’App Store ne répond pas. Réessayez dans un instant.',
  buyingPlay: 'Ouverture de Google Play…',
  storeNotAnsweringPlay: 'Google Play ne répond pas. Réessayez dans un instant.',
  notInAppYet: 'Bientôt dans l’app',
  notInAppYetFine: 'Ce moteur n’est pas encore en vente dans l’app. La démo est à vous dès aujourd’hui.',
  linkTitle: 'Connectez votre Tesla',
  linkDone: '✓ C’est fait',
  linkDoneBody: 'Votre Tesla est connectée en tant que {email}. Regardez l’écran de la voiture. Vous pouvez fermer ceci.',
  linkHint: 'Juste votre e-mail, pas de mot de passe. Première fois ? Cela crée votre compte.',
  signInBtn: 'SE CONNECTER',
  errSendCode: 'Nous n’avons pas pu envoyer le code. Vérifiez l’e-mail et réessayez.',
  errBadCode: 'Ce code n’est pas le bon.',
  engineStopHint: 'Arrêtez la voiture pour couper le moteur',
  demoWord: 'DÉMO',
  demoSub: 'gratuit',
  demoFine: 'Le moteur complet, bridé à {cap} km/h · quelques minutes par jour',
  purchase: 'ACHETER',
  purchaseFine: 'Paiement unique · le moteur complet, sans limite · à vous dans chaque voiture',
  oneTime: '{price} · paiement unique',
  savedToAccount: '✓ {email} · la voiture est enregistrée sur ce compte',
  unlockNote: 'Débloqué ici dès le paiement. Le moteur appartient à votre compte : connectez-vous avec le même e-mail dans n’importe quelle voiture ou sur n’importe quel téléphone et il est là.',

  signInSignUp: 'CONNEXION · INSCRIPTION',
  notifyMe: 'Prévenez-moi quand un nouveau moteur sort',
  wheelHintTitle: 'BOÎTE MANUELLE',
  wheelHintBody: 'Une pression sur la molette gauche du volant. Le sens, c’est la voiture qui le décide, pas vous :',
  wheelHintUp: 'En accélérant · rapport supérieur',
  wheelHintDown: 'En ralentissant · rapport inférieur',
  wheelHintPaddles: 'Les palettes à l’écran font pareil.',
  wheelHintOk: 'COMPRIS',
  wheelHintNever: 'Ne plus afficher',
  termsLine: 'En continuant vous acceptez les {terms} et la {privacy}.',
  termsWord: 'Conditions',
  privacyWord: 'Politique de confidentialité',
  accountCreated: 'Compte créé',
  welcomeBack: 'Bon retour',
  followCreated: 'vos voitures et réglages suivront cet e-mail partout',
  followBack: 'vos voitures et réglages sont de retour',
  scanPhone: 'Scannez avec votre téléphone.',
  scanPhoneBody: 'Connectez-vous là-bas et cet écran se connecte tout seul.',
  orTypeHere: 'ou tapez-le ici',
  emailIsAccount: 'Votre e-mail {is} le compte, pas de mot de passe. Vous en avez déjà un ? Même case. Première fois ? Le code le crée.',
  is: 'est',
  continueBtn: 'CONTINUER',
  sending: 'ENVOI…',
  weEmailed: 'Nous avons envoyé un code à {email}.',
  verify: 'VÉRIFIER',
  checking: 'VÉRIFICATION…',
  changeEmail: 'Changer d’e-mail',
};

const de: Dict = {
  menu: 'Menü',
  account: 'Konto',
  signIn: 'Anmelden',
  logOut: 'Abmelden',
  language: 'Sprache',
  languageAuto: 'Automatisch',
  languageAutoNote: 'Folgt deinem Telefon: {name}',
  admin: 'Admin',
  adminPanel: 'Admin-Bereich',
  diagnostics: 'Diagnose',
  change: 'ändern',

  tagline: 'Immersive Motoren für dein Auto',
  nextEngine: 'Nächster Motor',
  comingSoon: 'Demnächst',
  comingSoonBody: 'Weitere Motoren stehen auf dem Prüfstand. Melde dich an und hör sie als Erster.',
  comingUp: 'IN KÜRZE',
  free: 'GRATIS',
  owned: 'GEKAUFT',
  howItWorks: 'So funktioniert’s',
  step1Title: 'Web oder App öffnen',
  step1Body: 'roardrive.com im Browser deines Autos oder die RoarDrive-App auf dem Handy',
  step2Title: 'Motor wählen',
  step2Body: 'echter Rennwagen-Sound',
  step3Title: 'Fahren',
  step3Body: 'deine Geschwindigkeit macht den Rest',
  questions: 'Fragen',
  q1: 'Funktioniert das in jedem Auto?',
  a1: 'Ja. GPS reicht, Hardware ist optional.',
  q2: 'Ist das ein Abo?',
  a2: 'Nein. Einmalzahlung pro Motor, vorher gratis testen.',
  q3: 'Tesla-Bildschirm?',
  a3: 'Dafür optimiert: Vollbild-Cockpit, aktualisiert sich selbst.',
  q4: 'Lenkt das ab?',
  a4: 'Während der Fahrt wird nichts bedient. Im Stand einrichten, dann einfach fahren.',
  wallLine: 'Diese Wand ist echten Fahrern vorbehalten.',
  wallHow: 'Schon gefahren? Deine Meinung kommt zuerst:',
  terms: 'AGB',
  privacy: 'Datenschutz',
  cookies: 'Cookies',
  gdpr: 'DSGVO-Rechte',
  legalNotice: 'Impressum',
  drawerSignInLead: 'Eine E-Mail, kein Passwort.',
  drawerSignInBody: 'Deine Käufe und Einstellungen folgen dir in jedes Auto. Melde dich hier an oder scanne einen QR-Code mit dem Handy.',
  drawerAccountBody: 'Deine Motoren und Einstellungen folgen dieser E-Mail in jedes Auto.',
  dangerZone: 'Gefahrenzone',
  deleteAccount: 'Konto löschen',
  deleting: 'Wird gelöscht…',
  deleteFailed: 'Löschen nicht möglich: dieses Auto ist nicht mit dem Konto verknüpft. Melde dich hier zuerst an oder schreib an privacy@roardrive.com.',
  deleteConfirm: 'Dein ROARDRIVE-Konto löschen?\n\nE-Mail, verknüpfte Autos, Einstellungen und Rundenzeiten werden gelöscht. Gekaufte Motoren werden entkoppelt und lassen sich nicht auf ein anderes Konto übertragen. Das lässt sich nicht rückgängig machen.',
  deletedOk: 'Konto gelöscht.',

  pitBox: 'Box',
  drive: 'FAHREN',
  drivingSource: 'Geschwindigkeitsquelle',
  drivingSourceWhy: 'Der Klang folgt deiner echten Geschwindigkeit. Je schneller wir sie lesen, desto enger sitzt er an deinem rechten Fuß. Wähle alles, was du hast: der Motor nimmt immer die beste aktive Quelle.',
  drivingOn: 'Läuft gerade mit',
  proHw: 'Pro-Hardware',
  instant: 'Sofort',
  proHwMeta: '25 Hz · überhaupt keine Verzögerung',
  proHwDesc: 'RaceBox oder OBD-II.',
  proHwDescDemo: 'RaceBox oder OBD-II. Beim Motor dabei.',
  fullEngine: 'Voller Motor',
  teslaBrowser: 'Tesla-Browser im Auto',
  fast: 'Schnell',
  teslaMeta: '10 Hz · das GPS des Autos',
  teslaDesc: 'Öffne {site} auf dem Bildschirm des Tesla.',
  teslaFine: 'Öffne es auf dem Tesla-Bildschirm und das hier wird zur Abspieltaste.',
  playOnScreen: '▶ Auf dem Autobildschirm abspielen',
  sentToScreen: '✓ Gesendet. Es läuft auf dem Autobildschirm.',
  commander: 'Enhauto Commander',
  experimental: 'Experimentelle Funktion',
  experimentalSoon: 'Experimentelle Funktion · demnächst',
  commanderLead: 'Noch eine Ebene Immersion: der Motor hört das Auto selbst. Schalte den Tesla von {n} auf {d} und er legt den 1. Gang ein, {doors}, {lights} und {wipers} spielen mit, und die {launch} schärft sich selbst, wenn du 5 Sekunden voll auf der Bremse stehst.',
  doors: 'Türen',
  lights: 'Licht',
  wipers: 'Scheibenwischer',
  launchControl: 'Launch Control',
  cmStep1: 'S3XY-App: Commander, {scan}. Einmal, dann nie wieder.',
  cmStep2: 'Handy-WLAN: verbinde dich mit {ssid}, Passwort {pass}. Das WLAN selbst, nicht die S3XY-App: die koppelt über Bluetooth, RoarDrive liest den CAN über dieses Netz.',
  cmStep3: 'iPhone-Einstellungen, RoarDrive, {local} an. Einmal abgelehnt, fragt iOS nie wieder und blockiert still.',
  cmNoReply: 'Noch keine Antwort.',
  cmNoReplyBody: 'Einer der drei Punkte oben fehlt.',
  waitingApp: 'Wartet auf die Handy-App',
  connecting: 'Verbindet…',
  linkedWaiting: 'Verbunden, wartet auf CAN',
  tapToConnect: 'Zum Verbinden tippen',
  optional: 'Optional',
  answering: 'Antwortet',
  sim: 'SIM',
  simFree: 'Gratis',
  simMeta: 'ohne Auto · ohne GPS',
  simDesc: 'Fahr den Motor gleich hier: halte THROTTLE und BRAKE auf dem Bildschirm gedrückt.',
  simAdminName: 'SIM ADMIN',
  simAdminDesc: 'Der Simulator mit Session-Log, Audiopegeln und Telemetrie-Anzeigen.',
  phoneGps: 'Handy-GPS',
  backup: 'Reserve',
  delay: 'Verzögerung',
  phoneMeta: '{hz} · das GPS des Handys',
  phoneDescBackup: 'Läuft immer im Hintergrund als Reserve. Was auch ausfällt, das hält den Motor am Leben.',
  phoneDescAlone: 'Jedes Auto. Null Einrichtung.',
  detecting: 'wird gemessen…',
  appRow: 'RoarDrive-App auf deinem Handy',
  appRowBody: 'Öffne sie, melde dich {who} an und wähle {pro}.',
  appRowWithEmail: 'als {email}',
  appRowNoEmail: 'mit deiner E-Mail',
  appRowSignInHere: 'Oder {link}, damit beide Seiten dasselbe Konto teilen.',
  appRowSignInLink: 'melde dich auf diesem Bildschirm an',
  linked: '✓ Verbunden',
  obdRow: 'OBD-II-Adapter',
  obdStep1: 'Steck einen ELM327-Adapter mit Bluetooth LE in die OBD-II-Buchse (meist unter dem Lenkrad).',
  obdStep2: 'Zündung an.',
  obdStep3: 'Er wird von selbst gefunden. Er liest den Tacho des Autos, also gibt es keinerlei Satellitenverzögerung, und mit seiner Drehzahl schaltet der Motor genau dann, wenn du schaltest.',
  obdBuyTitle: 'Welchen Adapter kaufe ich?',
  obdBuyBle: 'Bluetooth LE, auch BLE oder Bluetooth 4.0 geschrieben. Steht auf der Packung nur „Bluetooth“, ist es das alte 3.0 und das iPhone sieht ihn gar nicht.',
  obdBuyElm: 'ELM327-kompatibel, das sind fast alle.',
  obdBuyWifi: 'Keine WLAN-Adapter: die belegen die Verbindung des Telefons und wir lesen sie nicht.',
  obdBuyPrice: 'Das Teil kostet 25 bis 45 Euro. Wer mehr zahlt, zahlt für Werkstattdiagnose, die wir nie nutzen.',
  obdBuyNames: 'Diese passen: Veepeak OBDCheck BLE+, Vgate iCar Pro BLE 4.0, vLinker MC+. Achte auf das Plus: der vLinker FD ohne Plus ist Bluetooth 3.0 und taucht auf einem iPhone nie auf.',
  obdBuyEv: 'Elektroauto? Viele geben die Geschwindigkeit an der Standardbuchse nicht heraus. Wenn deins elektrisch ist, ist die RaceBox der sichere Kauf.',
  learnTitle: 'Dein Getriebe, gemessen',
  learnTitleSim: 'Im Simulator gemessen',
  learnProgress: '{gears} Gänge · {min} Min',
  learnHint: 'Steck einen OBD-II-Adapter ein und fahr wie immer. Die App liest deine Geschwindigkeit und deine Drehzahl und findet dein Getriebe von selbst: nichts einzutragen, nichts zu halten, keinen Gang ausdrehen.',
  learnHintSim: 'Fahr zwei Minuten im Simulator und das hier füllt sich mit den Gängen des Motors selbst. Es ist dieselbe Messung, die der OBD-Adapter im echten Auto macht.',
  learnKeepGoing: 'Fahr weiter: die mittleren Gänge brauchen ein paar Minuten normale Straße.',
  learnReady: 'Aus deiner eigenen Fahrt gemessen, bis {rpm} U/min.',
  learnUse: 'Diese Gänge nehmen',
  learnForget: 'vergessen und neu anfangen',
  learnFollowing: '● Wir folgen deinem Auto: du fährst im {gear}. Gang, der Motor auch.',
  driveSafe: 'sicher',
  audioDeniedTitle: 'Der Motor konnte nicht laden',
  audioDeniedBody: 'Die Aufnahmen sind nicht angekommen, also läuft nichts. Meist sind die freien Durchläufe des Tages aufgebraucht, oder die Verbindung ist schlecht. Melde dich an, wenn dir dieser Motor gehört, oder versuch es nochmal.',
  audioDeniedRetry: 'Nochmal versuchen',
  gpsDeniedTitle: 'Standort ist blockiert',
  gpsDeniedBody: 'Der Browser des Autos gibt seine Position nicht weiter, also hat der Motor nichts, dem er folgen kann. Meist wurde der Standort für diese Seite einmal abgelehnt: im Browsermenü die Browserdaten löschen, roardrive.com neu öffnen und den Standort erlauben, wenn gefragt wird. Und er lebt nur in Fahrt auf: im Stand läuft er im Leerlauf.',
  gpsDeniedRetry: 'Nochmal versuchen',
  yes: 'Ja',
  no: 'Nein',
  learnFollowLabel: 'Schalten, wenn ich schalte',
  learnUseWhy: 'Gespeichert werden sie für die Fahrten ohne Adapter: dann schaltet der Motor dort, wo dein Auto schaltet.',
  learnNotRacebox: 'Das kann nur ein OBD-II-Adapter. Eine RaceBox gibt Position und Geschwindigkeit, sie liest den Motor nicht und sieht deine Gänge daher nicht.',
  raceboxRow: 'RaceBox',
  rbStep1: 'Schalte die RaceBox ein.',
  rbStep2: 'Schließe die offizielle RaceBox-App (Bluetooth erlaubt nur eine Verbindung).',
  rbStep3: 'Sie verbindet sich von selbst{state}. Einmal gekoppelt, geht es danach automatisch.',
  stateSearching: ' (sucht…)',
  stateFound: ' (gefunden, verbindet…)',
  stateSatellites: ' (wartet auf Satelliten…)',
  live: '✓ Live',
  connectedIgnition: 'Verbunden, Zündung an?',
  connectedSatellites: 'Verbunden · wartet auf Satelliten…',
  searching: 'Sucht…',
  foundConnecting: 'Gefunden, verbindet…',
  fuseLive: 'Live · {hz} Nachr./s. RaceBox und OBD-II verschmelzen, schließ beide an.',
  fuseFoot: 'Sie verschmelzen. Schließ ruhig mehrere an.',
  startingBridge: 'Hardware-Brücke startet… (Bluetooth an)',
  openBridge: 'Brücke öffnen (ältere App-Version)',
  findWiper: 'Finde das Wischer-Bit.',
  findWiperBody: 'Abwechselnd, jeweils etwa 5 s warten: Wischer AUS tippe {off}, Wischer AN tippe {on}, dann aus, dann wieder an. Zwei Paare genügen.',
  off: 'aus',
  on: 'an',
  reset: 'zurücksetzen',

  gearRatio: 'Getriebeabstufung',
  gearRatioWhy: 'Dein Auto schaltet dort, wo ein Rennwagen es nicht täte. Sag uns deins und der Motor schaltet mit dir.',
  offline: 'Ohne Netz',
  offlineWhy: 'Der Motor liegt auf diesem Gerät und fährt allein mit GPS: Tunnel, Bergstraßen, gar kein Signal. Er lädt sich von selbst herunter.',
  offlineChecking: 'Wird geprüft…',
  offlineDownloading: 'Motor wird geladen',
  offlineProgress: '{done} von {total} MB',
  offlineReady: 'Bereit ohne Netz',
  offlineReadyMeta: '{mb} MB auf diesem Gerät · nur GPS',
  offlineFailed: 'Der Download ist nicht fertig geworden',
  offlineFailedMeta: 'Es fehlen noch Dateien. Versuch es mit besserem Empfang noch einmal.',
  offlineRetry: 'Noch einmal',
  offlineNoNet: 'Gerade kein Netz',
  offlineNoNetMeta: 'Er lädt von selbst, sobald du wieder online bist.',
  realistic: 'Realistisch',
  yourCarTab: 'Dein Auto',
  realisticNote: 'Jeder Motor schaltet genau wie sein echtes Auto: sein eigenes Getriebe, egal was du fährst.',
  topSpeedPerGear: 'Höchstgeschwindigkeit pro Gang · {unit}',
  speedAtLimiter: 'Geschwindigkeit am Begrenzer pro Gang · {unit}',
  evLadderNote: 'das Getriebe des Motors, skaliert · {unit}',
  ev: 'Elektro',
  ice: 'Verbrenner',
  topSpeed: 'Höchstgeschwindigkeit',
  topSpeedOf: '{speed} Spitze',
  carLimit: '{speed} · Grenze des {name}',
  brandPick: 'Marke…',
  myOwnGears: 'Meine eigenen Gänge',
  yourGears: 'Deine Gänge',
  editedSuffix: '{model}, bearbeitet',
  backToCar: 'Zurück zum {model}',
  addGear: '+ Gang',
  removeGear: '− Gang',
  tapToChange: 'Tippe eine Geschwindigkeit an, um sie zu ändern. Maximum pro Gang = das des Motors: {limits} km/h',

  demoLine: 'Simulator gratis · auf der Straße {min} Min heute, Boxenbegrenzer {cap} km/h · ',
  demoLineWeb: 'Auf der Straße heute {min} Min, Boxenlimit {cap} km/h · ',
  demoLineOpen: 'Simulator gratis · offene Woche: auf der Straße {min} Min heute, ohne Begrenzer · ',
  demoLineOpenWeb: 'Offene Woche: auf der Straße heute {min} Min, ohne Begrenzer · ',
  demoUsed: 'Simulator gratis · die Minuten auf der Straße sind heute aufgebraucht · ',
  demoBadge: 'DEMO · Begrenzer {cap} km/h · max. Gang {gear} · {time}',
  demoBadgeOpen: 'DEMO · {time}',
  demoBadgeBuy: 'FREISCHALTEN',
  demoGearHeld: 'Demo: Das Getriebe bleibt im {gear}. Gang (Begrenzer {cap} km/h). Schalte diesen Motor für alle Gänge frei.',
  demoUsedWeb: 'Die Minuten auf der Straße sind heute aufgebraucht · ',
  unlockFor: 'freischalten für {price}',
  lastSession: 'LETZTE SESSION',
  best: 'beste',
  laps: '{n} Runden',
  lap1: '1 Runde',
  teslaDetected: '🚗 Tesla erkannt: {name}',
  recommendCarBrowser: 'Wir empfehlen, im Browser des Autos zu fahren: öffne dort {site}. Besseres GPS, und hier erscheint eine {play}-Taste.',
  playThere: '„dort abspielen“',
  carScreenNotLinked: 'Autobildschirm nicht verbunden. Öffne {site} im Tesla-Browser und hier erscheint von selbst eine {play}-Taste.',

  tapToEnter: 'ZUM STARTEN TIPPEN',
  loading: 'LÄDT…',
  volumeNote: 'Beim ersten Mal die Lautstärke moderat halten',
  teslaAudioNote: 'Tesla: der Browser-Ton ersetzt die laufende Medienquelle',
  drivingInput: 'Geschwindigkeitsquelle',
  rotatePhone: 'Dreh dein Handy',
  hardwareGuide: 'Anleitung zur Hardware →',
  units: 'Einheiten',
  metric: 'Metrisch',
  imperial: 'Imperial',
  demoOver: 'DEMO VORBEI',
  unlockFull: 'Ganzer Motor · {price} einmalig',
  payOnPhone: 'Sichere Zahlung über Stripe · der Motor wird auf diesem Bildschirm freigeschaltet, sobald du bezahlst',
  backToEngines: '← Zurück zu den Motoren',

  demoFineLeft: 'Simulator gratis, ohne Uhr · auf der Straße {min} Min heute, Begrenzer {cap} km/h',
  demoFineLeftWeb: 'Auf der Straße heute {min} Min, Limit {cap} km/h',
  demoFineLeftOpen: 'Simulator gratis, ohne Uhr · offene Woche: auf der Straße {min} Min heute, ohne Begrenzer',
  demoFineLeftOpenWeb: 'Offene Woche: auf der Straße heute {min} Min, ohne Limit',
  demoFineUsed: 'Simulator gratis, ohne Uhr · die {min} Min auf der Straße sind heute aufgebraucht',
  demoFineUsedWeb: 'Die {min} Min auf der Straße sind heute aufgebraucht',
  buyInApp: 'Kaufen · {price}',
  buying: 'App Store wird geöffnet…',
  buyCancelled: 'Kauf abgebrochen.',
  buyDone: 'Freigeschaltet. Viel Spaß.',
  purchases: 'Käufe',
  restorePurchases: 'Käufe wiederherstellen',
  restoreNote: 'Neues Handy oder neu installiert? Deine Motoren kommen hier zurück. Es wird nichts erneut berechnet: auf dem iPhone fragt die App Apple, was dir gehört, überall sonst zählt dein Konto.',
  restoring: 'Wird wiederhergestellt…',
  restoreDone: '✓ Dein Kauf ist zurück. Viel Spaß.',
  restoreNone: 'Auf diesem Gerät gibt es nichts Neues wiederherzustellen. Hast du mit einer anderen E-Mail bezahlt, melde dich damit an, der Motor folgt.',
  restoreFailed: 'Wiederherstellung gerade nicht möglich. Versuch es gleich noch einmal.',
  buyFailed: 'Der Kauf ist nicht durchgegangen. Es wurde nichts abgebucht.',
  pqrScan: 'Zum Bezahlen mit dem Handy scannen · schaltet sich von selbst frei',
  pqrPreparing: 'Sichere Kasse wird vorbereitet…',
  pqrUnlocked: '✓ Freigeschaltet',
  pqrNotConfigured: 'Zahlungen sind noch nicht eingerichtet.',
  pqrError: 'Die Kasse ist gerade nicht verfügbar. Versuch es später.',
  buyNeedAccount: 'Zuerst dein Konto',
  buyNeedAccountBody: 'Der Motor gehört zum Konto, nicht zu diesem Bildschirm. Melde dich mit deiner E-Mail an, dann ist er im Auto, auf dem Handy und nach jeder Neuinstallation wieder da. Ein Code, kein Passwort.',
  buyNeedAccountBtn: 'KONTO ERSTELLEN · ANMELDEN',
  payNow: '{price} BEZAHLEN',
  pqrPayNote: 'Sichere Zahlung über Stripe · die Quittung geht an {email}',
  storeNotAnswering: 'Der App Store antwortet nicht. Versuch es gleich noch einmal.',
  buyingPlay: 'Google Play wird geöffnet…',
  storeNotAnsweringPlay: 'Google Play antwortet nicht. Versuch es gleich noch einmal.',
  notInAppYet: 'Bald in der App',
  notInAppYetFine: 'Dieser Motor ist in der App noch nicht erhältlich. Die Demo gehört dir schon heute.',
  linkTitle: 'Melde deinen Tesla an',
  linkDone: '✓ Fertig',
  linkDoneBody: 'Dein Tesla ist als {email} angemeldet. Schau auf den Autobildschirm. Das hier kannst du schließen.',
  linkHint: 'Nur deine E-Mail, kein Passwort. Zum ersten Mal? Das legt dein Konto an.',
  signInBtn: 'ANMELDEN',
  errSendCode: 'Wir konnten den Code nicht senden. Prüf die E-Mail und versuch es noch einmal.',
  errBadCode: 'Dieser Code stimmt nicht.',
  engineStopHint: 'Halte an, um den Motor auszuschalten',
  demoWord: 'DEMO',
  demoSub: 'gratis',
  demoFine: 'Der volle Motor, begrenzt auf {cap} km/h · ein paar Minuten am Tag',
  purchase: 'KAUFEN',
  purchaseFine: 'Einmalig · der volle Motor, ohne Grenzen · deiner in jedem Auto',
  oneTime: '{price} · einmalig',
  savedToAccount: '✓ {email} · das Auto ist in diesem Konto gespeichert',
  unlockNote: 'Wird hier freigeschaltet, sobald du bezahlst. Der Motor gehört zu deinem Konto: mit derselben E-Mail in jedem Auto oder auf jedem Handy anmelden, und er ist da.',

  signInSignUp: 'ANMELDEN · REGISTRIEREN',
  notifyMe: 'Sag mir Bescheid, wenn ein neuer Motor kommt',
  wheelHintTitle: 'MANUELLES GETRIEBE',
  wheelHintBody: 'Ein Druck auf das linke Rad am Lenkrad. In welche Richtung, entscheidet das Auto, nicht du:',
  wheelHintUp: 'Beim Beschleunigen · hoch',
  wheelHintDown: 'Beim Bremsen · runter',
  wheelHintPaddles: 'Die Paddles am Bildschirm machen dasselbe.',
  wheelHintOk: 'ALLES KLAR',
  wheelHintNever: 'Nicht mehr anzeigen',
  termsLine: 'Mit dem Fortfahren akzeptierst du die {terms} und die {privacy}.',
  termsWord: 'Bedingungen',
  privacyWord: 'Datenschutzerklärung',
  accountCreated: 'Konto erstellt',
  welcomeBack: 'Willkommen zurück',
  followCreated: 'deine Autos und Einstellungen folgen dieser E-Mail überallhin',
  followBack: 'deine Autos und Einstellungen sind zurück',
  scanPhone: 'Mit dem Handy scannen.',
  scanPhoneBody: 'Melde dich dort an und dieser Bildschirm meldet sich von selbst an.',
  orTypeHere: 'oder hier eintippen',
  emailIsAccount: 'Deine E-Mail {is} das Konto, kein Passwort. Schon eins? Dasselbe Feld. Zum ersten Mal? Der Code legt es an.',
  is: 'ist',
  continueBtn: 'WEITER',
  sending: 'WIRD GESENDET…',
  weEmailed: 'Wir haben einen Code an {email} geschickt.',
  verify: 'BESTÄTIGEN',
  checking: 'WIRD GEPRÜFT…',
  changeEmail: 'E-Mail ändern',
};

const it: Dict = {
  menu: 'Menu',
  account: 'Account',
  signIn: 'Accedi',
  logOut: 'Esci',
  language: 'Lingua',
  languageAuto: 'Automatica',
  languageAutoNote: 'Segue il telefono: {name}',
  admin: 'Admin',
  adminPanel: 'Pannello admin',
  diagnostics: 'Diagnostica',
  change: 'cambia',

  tagline: 'Motori immersivi per la tua auto',
  nextEngine: 'Prossimo motore',
  comingSoon: 'In arrivo',
  comingSoonBody: 'Altri motori sono al banco prova. Accedi e sarai il primo a sentirli.',
  comingUp: 'IN ARRIVO',
  free: 'GRATIS',
  owned: 'TUO',
  howItWorks: 'Come funziona',
  step1Title: 'Apri il sito o l’app',
  step1Body: 'roardrive.com nel browser dell’auto, o l’app RoarDrive sul telefono',
  step2Title: 'Scegli un motore',
  step2Body: 'suono vero di un’auto da corsa',
  step3Title: 'Guida',
  step3Body: 'la tua velocità fa il resto',
  questions: 'Domande',
  q1: 'Funziona su qualsiasi auto?',
  a1: 'Sì. Basta il GPS, l’hardware è opzionale.',
  q2: 'È un abbonamento?',
  a2: 'No. Pagamento unico per motore, con demo gratuita prima.',
  q3: 'Schermo della Tesla?',
  a3: 'Ottimizzato per lui: cruscotto a schermo intero che si aggiorna da solo.',
  q4: 'Distrae?',
  a4: 'Non si tocca nulla mentre guidi. Si imposta da fermi, poi si guida e basta.',
  wallLine: 'Questo muro è riservato ai piloti veri.',
  wallHow: 'L’hai provato? Il tuo va per primo:',
  terms: 'Termini',
  privacy: 'Privacy',
  cookies: 'Cookie',
  gdpr: 'Diritti GDPR',
  legalNotice: 'Note legali',
  drawerSignInLead: 'Una email, nessuna password.',
  drawerSignInBody: 'Acquisti e impostazioni ti seguono in ogni auto. Accedi qui, o scansiona un QR con il telefono.',
  drawerAccountBody: 'I tuoi motori e le impostazioni seguono questa email in ogni auto.',
  dangerZone: 'Zona pericolosa',
  deleteAccount: 'Elimina account',
  deleting: 'Eliminazione…',
  deleteFailed: 'Impossibile eliminare: quest’auto non è collegata a quell’account. Accedi qui prima, o scrivi a privacy@roardrive.com.',
  deleteConfirm: 'Eliminare il tuo account ROARDRIVE?\n\nVengono cancellati email, auto collegate, impostazioni e tempi sul giro. I motori acquistati vengono scollegati e non si possono spostare su un altro account. Non si torna indietro.',
  deletedOk: 'Account eliminato.',

  pitBox: 'Box',
  drive: 'GUIDA',
  drivingSource: 'Fonte di velocità',
  drivingSourceWhy: 'Il suono segue la tua velocità reale. Più in fretta riusciamo a leggerla, più resta incollato al piede destro. Scegli tutte quelle che hai: il motore usa sempre la migliore attiva.',
  drivingOn: 'Adesso con',
  proHw: 'Hardware Pro',
  instant: 'Istantaneo',
  proHwMeta: '25 Hz · nessun ritardo',
  proHwDesc: 'RaceBox o OBD-II.',
  proHwDescDemo: 'RaceBox o OBD-II. Incluso con il motore.',
  fullEngine: 'Motore completo',
  teslaBrowser: 'Browser della Tesla',
  fast: 'Veloce',
  teslaMeta: '10 Hz · il GPS dell’auto',
  teslaDesc: 'Apri {site} sullo schermo della Tesla.',
  teslaFine: 'Aprilo sullo schermo della Tesla e questo diventa il pulsante di riproduzione.',
  playOnScreen: '▶ Mandalo sullo schermo dell’auto',
  sentToScreen: '✓ Inviato. Sta girando sullo schermo dell’auto.',
  commander: 'Enhauto Commander',
  experimental: 'Funzione sperimentale',
  experimentalSoon: 'Funzione sperimentale · in arrivo',
  commanderLead: 'Un altro strato di immersione: il motore ascolta l’auto stessa. Porta la Tesla da {n} a {d} e innesta la 1ª, {doors}, {lights} e {wipers} entrano in gioco, e il {launch} si arma da solo quando tieni il freno a fondo per 5 secondi.',
  doors: 'porte',
  lights: 'luci',
  wipers: 'tergicristalli',
  launchControl: 'launch control',
  cmStep1: 'App S3XY: Commander, {scan}. Una volta e mai più.',
  cmStep2: 'Wi-Fi del telefono: collegati a {ssid}, password {pass}. Il Wi-Fi in sé, non l’app S3XY: quella si accoppia via Bluetooth e RoarDrive legge il CAN su questa rete.',
  cmStep3: 'Impostazioni iPhone, RoarDrive, {local} attivo. Se lo neghi una volta, iOS non lo chiede più e lo blocca in silenzio.',
  cmNoReply: 'Ancora nessuna risposta.',
  cmNoReplyBody: 'Uno dei tre punti qui sopra non è fatto.',
  waitingApp: 'In attesa dell’app',
  connecting: 'Connessione…',
  linkedWaiting: 'Collegato, in attesa del CAN',
  tapToConnect: 'Tocca per collegare',
  optional: 'Opzionale',
  answering: 'Risponde',
  sim: 'SIM',
  simFree: 'Gratis',
  simMeta: 'senza auto · senza GPS',
  simDesc: 'Guida il motore qui: tieni premuti THROTTLE e BRAKE sullo schermo.',
  simAdminName: 'SIM ADMIN',
  simAdminDesc: 'Il simulatore con il log di sessione, i livelli audio e le targhette di telemetria.',
  phoneGps: 'GPS del telefono',
  backup: 'Riserva',
  delay: 'Ritardo',
  phoneMeta: '{hz} · il GPS del telefono',
  phoneDescBackup: 'Sempre attivo sotto, come riserva. Qualunque cosa cada, tiene vivo il motore.',
  phoneDescAlone: 'Qualsiasi auto. Zero configurazione.',
  detecting: 'misurazione…',
  appRow: 'App RoarDrive sul telefono',
  appRowBody: 'Aprila, accedi {who} e scegli {pro}.',
  appRowWithEmail: 'come {email}',
  appRowNoEmail: 'con la tua email',
  appRowSignInHere: 'Oppure {link} così i due lati condividono l’account.',
  appRowSignInLink: 'accedi da questo schermo',
  linked: '✓ Collegata',
  obdRow: 'Adattatore OBD-II',
  obdStep1: 'Inserisci un adattatore ELM327 con Bluetooth LE nella presa OBD-II (di solito sotto il volante).',
  obdStep2: 'Quadro acceso.',
  obdStep3: 'Viene trovato da solo. Legge il tachimetro dell’auto, quindi non c’è alcun ritardo satellitare.',
  obdBuyTitle: 'Quale adattatore compro?',
  obdBuyBle: 'Bluetooth LE, scritto anche BLE o Bluetooth 4.0. Se sulla scatola c’è solo «Bluetooth» è il vecchio 3.0 e l’iPhone non lo vede nemmeno.',
  obdBuyElm: 'Compatibile ELM327, come lo sono quasi tutti.',
  obdBuyWifi: 'Non quelli Wi-Fi: si prendono la connessione del telefono e noi non li leggiamo.',
  obdBuyPrice: 'È un pezzo da 25 a 45 euro. Pagare di più significa pagare diagnosi da officina che non usiamo.',
  obdBuyNames: 'Questi vanno bene: Veepeak OBDCheck BLE+, Vgate iCar Pro BLE 4.0, vLinker MC+. Attenzione al più: il vLinker FD senza il più è Bluetooth 3.0 e su iPhone non compare mai.',
  obdBuyEv: 'Auto elettrica? Molte non pubblicano la velocità sulla presa standard. Se la tua è elettrica, l’acquisto sicuro è il RaceBox.',
  learnTitle: 'Il tuo cambio, misurato',
  learnTitleSim: 'Misurato nel simulatore',
  learnProgress: '{gears} marce · {min} min',
  learnHint: 'Collega un adattatore OBD-II e guida come sempre. L’app legge la tua velocità e i tuoi giri e ricava da sola il tuo cambio: niente da compilare, niente da tenere, nessuna marcia da tirare.',
  learnHintSim: 'Guida un paio di minuti nel simulatore e questo si riempie con le marce del motore stesso. È la stessa misura che fa l’adattatore OBD in un’auto vera.',
  learnKeepGoing: 'Continua a guidare: le marce centrali vogliono qualche minuto di strada normale.',
  learnReady: 'Misurato sulla tua guida, fino a {rpm} giri.',
  learnUse: 'Usa queste marce',
  learnForget: 'dimentica e ricomincia',
  learnFollowing: '● Stiamo seguendo la tua auto: sei in {gear}ª e anche il motore.',
  driveSafe: 'in sicurezza',
  audioDeniedTitle: 'Il motore non si è caricato',
  audioDeniedBody: 'Le registrazioni non sono arrivate, quindi non suona niente. Di solito sono finiti gli ascolti gratuiti del giorno, o la connessione è scarsa. Accedi se questo motore è tuo, oppure riprova.',
  audioDeniedRetry: 'Riprova',
  gpsDeniedTitle: 'La posizione è bloccata',
  gpsDeniedBody: 'Il browser dell\'auto non consegna la sua posizione, quindi il motore non ha nulla da seguire. Di solito la posizione è stata rifiutata una volta per questo sito: nel menu del browser, cancella i dati di navigazione, riapri roardrive.com e consenti la posizione quando viene chiesta. E ricorda che prende vita solo in movimento: da fermo, resta al minimo.',
  gpsDeniedRetry: 'Riprova',
  yes: 'Sì',
  no: 'No',
  learnFollowLabel: 'Cambiare marcia quando cambio io',
  learnUseWhy: 'Salvarle serve per i viaggi senza adattatore: il motore cambierà dove cambia la tua auto.',
  learnNotRacebox: 'Questo lo può fare solo un adattatore OBD-II. Il RaceBox dà posizione e velocità, non legge il motore, quindi non vede le tue marce.',
  raceboxRow: 'RaceBox',
  rbStep1: 'Accendi il RaceBox.',
  rbStep2: 'Chiudi l’app ufficiale RaceBox (il Bluetooth accetta una sola connessione).',
  rbStep3: 'Si collega da solo{state}. Una volta accoppiato, dopo è automatico.',
  stateSearching: ' (ricerca…)',
  stateFound: ' (trovato, connessione…)',
  stateSatellites: ' (in attesa dei satelliti…)',
  live: '✓ In diretta',
  connectedIgnition: 'Collegato, quadro acceso?',
  connectedSatellites: 'Collegato · in attesa dei satelliti…',
  searching: 'Ricerca…',
  foundConnecting: 'Trovato, connessione…',
  fuseLive: 'In diretta · {hz} msg/s. RaceBox e OBD-II si fondono, collegali entrambi.',
  fuseFoot: 'Si fondono. Collegane più di uno insieme.',
  startingBridge: 'Avvio del ponte hardware… (Bluetooth acceso)',
  openBridge: 'Apri il ponte (versione vecchia dell’app)',
  findWiper: 'Trova il bit dei tergicristalli.',
  findWiperBody: 'Alterna, aspettando circa 5 s ogni volta: tergicristalli OFF tocca {off}, ON tocca {on}, poi off, poi di nuovo on. Due coppie bastano.',
  off: 'off',
  on: 'on',
  reset: 'azzera',

  gearRatio: 'Rapporti del cambio',
  gearRatioWhy: 'La tua auto cambia marcia dove un’auto da corsa non lo farebbe. Dicci qual è e il motore cambia con te.',
  offline: 'Senza copertura',
  offlineWhy: 'Il motore resta su questo dispositivo e guida con il solo GPS: gallerie, strade di montagna, nessun segnale. Si scarica da solo.',
  offlineChecking: 'Controllo…',
  offlineDownloading: 'Scaricamento del motore',
  offlineProgress: '{done} di {total} MB',
  offlineReady: 'Pronto senza copertura',
  offlineReadyMeta: '{mb} MB su questo dispositivo · solo GPS',
  offlineFailed: 'Il download non è finito',
  offlineFailedMeta: 'Mancano ancora alcuni file. Riprova con più segnale.',
  offlineRetry: 'Riprova',
  offlineNoNet: 'Adesso non c’è copertura',
  offlineNoNetMeta: 'Si scarica da solo appena torni online.',
  realistic: 'Realistico',
  yourCarTab: 'La tua auto',
  realisticNote: 'Ogni motore cambia esattamente come la sua auto vera: il suo cambio, qualunque cosa tu guidi.',
  topSpeedPerGear: 'velocità massima per marcia · {unit}',
  speedAtLimiter: 'velocità al limitatore per marcia · {unit}',
  evLadderNote: 'il cambio del motore, scalato · {unit}',
  ev: 'Elettrica',
  ice: 'Benzina',
  topSpeed: 'Velocità massima',
  topSpeedOf: '{speed} di punta',
  carLimit: '{speed} · limite della {name}',
  brandPick: 'Marca…',
  myOwnGears: 'Le mie marce',
  yourGears: 'Le tue marce',
  editedSuffix: '{model}, modificata',
  backToCar: 'Torna alla {model}',
  addGear: '+ marcia',
  removeGear: '− marcia',
  tapToChange: 'Tocca una velocità per cambiarla. Massimo per marcia = quello del motore: {limits} km/h',

  demoLine: 'Simulatore gratis · su strada {min} min oggi, limitatore box {cap} km/h · ',
  demoLineWeb: 'In strada {min} min oggi, limitatore {cap} km/h · ',
  demoLineOpen: 'Simulatore gratis · settimana aperta: su strada {min} min oggi, senza limitatore · ',
  demoLineOpenWeb: 'Settimana aperta: in strada {min} min oggi, senza limitatore · ',
  demoUsed: 'Simulatore gratis · i minuti su strada sono esauriti oggi · ',
  demoBadge: 'DEMO · limitatore {cap} km/h · marcia max {gear} · {time}',
  demoBadgeOpen: 'DEMO · {time}',
  demoBadgeBuy: 'SBLOCCA',
  demoGearHeld: 'Demo: il cambio si ferma alla {gear}ª (limitatore {cap} km/h). Sblocca questo motore per tutte le marce.',
  demoUsedWeb: 'I minuti in strada sono finiti per oggi · ',
  unlockFor: 'sblocca per {price}',
  lastSession: 'ULTIMA SESSIONE',
  best: 'migliore',
  laps: '{n} giri',
  lap1: '1 giro',
  teslaDetected: '🚗 Tesla rilevata: {name}',
  recommendCarBrowser: 'Consigliamo di guidare nel browser dell’auto: apri lì {site}. GPS migliore, e qui compare un pulsante {play}.',
  playThere: '«mandalo lì»',
  carScreenNotLinked: 'Schermo dell’auto non collegato. Apri {site} nel browser della Tesla e qui compare da solo un pulsante {play}.',

  tapToEnter: 'TOCCA PER ENTRARE',
  loading: 'CARICAMENTO…',
  volumeNote: 'La prima volta tieni il volume moderato',
  teslaAudioNote: 'Tesla: l’audio del browser sostituisce la sorgente in ascolto',
  drivingInput: 'Fonte di velocità',
  rotatePhone: 'Ruota il telefono',
  hardwareGuide: 'Guida alla configurazione hardware →',
  units: 'Unità',
  metric: 'Metrico',
  imperial: 'Imperiale',
  demoOver: 'DEMO FINITA',
  unlockFull: 'Motore completo · {price} pagamento unico',
  payOnPhone: 'Pagamento sicuro con Stripe · il motore si sblocca su questo schermo appena paghi',
  backToEngines: '← Torna ai motori',

  demoFineLeft: 'Simulatore gratis, senza orologio · su strada {min} min oggi, limitatore {cap} km/h',
  demoFineLeftWeb: 'In strada {min} min oggi, limitatore {cap} km/h',
  demoFineLeftOpen: 'Simulatore gratis, senza orologio · settimana aperta: su strada {min} min oggi, senza limitatore',
  demoFineLeftOpenWeb: 'Settimana aperta: in strada {min} min oggi, senza limitatore',
  demoFineUsed: 'Simulatore gratis, senza orologio · i {min} min su strada sono esauriti oggi',
  demoFineUsedWeb: 'I {min} min in strada sono finiti per oggi',
  buyInApp: 'Acquista · {price}',
  buying: 'Apertura dell’App Store…',
  buyCancelled: 'Acquisto annullato.',
  buyDone: 'Sbloccato. Buon divertimento.',
  purchases: 'Acquisti',
  restorePurchases: 'Ripristina acquisti',
  restoreNote: 'Hai cambiato telefono o reinstallato l’app? I tuoi motori tornano qui. Non si paga due volte: su iPhone si chiede ad Apple cosa possiedi, altrove conta il tuo account.',
  restoring: 'Ripristino…',
  restoreDone: '✓ Il tuo acquisto è tornato. Buon divertimento.',
  restoreNone: 'Non c’è nulla di nuovo da ripristinare su questo dispositivo. Se hai pagato con un’altra email, accedi con quella e il motore ti segue.',
  restoreFailed: 'Non è stato possibile ripristinare adesso. Riprova tra un momento.',
  buyFailed: 'L’acquisto non è andato a buon fine. Non è stato addebitato nulla.',
  pqrScan: 'Inquadra col telefono per pagare · si sblocca da solo',
  pqrPreparing: 'Preparazione del pagamento sicuro…',
  pqrUnlocked: '✓ Sbloccato',
  pqrNotConfigured: 'I pagamenti non sono ancora configurati.',
  pqrError: 'Il pagamento non è disponibile adesso. Riprova più tardi.',
  buyNeedAccount: 'Prima, il tuo account',
  buyNeedAccountBody: 'Il motore si salva nell’account, non in questo schermo. Accedi con la tua email e lo ritrovi in auto, sul telefono e dopo qualsiasi reinstallazione. Un codice, senza password.',
  buyNeedAccountBtn: 'CREA ACCOUNT · ACCEDI',
  payNow: 'PAGA {price}',
  pqrPayNote: 'Pagamento sicuro con Stripe · la ricevuta va a {email}',
  storeNotAnswering: 'L’App Store non risponde. Riprova tra un momento.',
  buyingPlay: 'Apertura di Google Play…',
  storeNotAnsweringPlay: 'Google Play non risponde. Riprova tra un momento.',
  notInAppYet: 'Presto nell’app',
  notInAppYetFine: 'Questo motore non è ancora in vendita nell’app. La demo è già tua.',
  linkTitle: 'Accedi sulla tua Tesla',
  linkDone: '✓ Fatto',
  linkDoneBody: 'La tua Tesla ha effettuato l’accesso come {email}. Guarda lo schermo dell’auto. Puoi chiudere questa pagina.',
  linkHint: 'Solo la tua email, nessuna password. Prima volta? Questo crea il tuo account.',
  signInBtn: 'ACCEDI',
  errSendCode: 'Non siamo riusciti a inviare il codice. Controlla l’email e riprova.',
  errBadCode: 'Questo codice non è giusto.',
  engineStopHint: 'Ferma l’auto per spegnere il motore',
  demoWord: 'DEMO',
  demoSub: 'gratis',
  demoFine: 'Il motore completo, limitato a {cap} km/h · qualche minuto al giorno',
  purchase: 'ACQUISTA',
  purchaseFine: 'Pagamento unico · il motore completo, senza limiti · tuo in ogni auto',
  oneTime: '{price} · pagamento unico',
  savedToAccount: '✓ {email} · l’auto è salvata su questo account',
  unlockNote: 'Si sblocca qui appena paghi. Il motore appartiene al tuo account: accedi con la stessa email in qualsiasi auto o telefono e c’è.',

  signInSignUp: 'ACCEDI · REGISTRATI',
  notifyMe: 'Avvisami quando esce un motore nuovo',
  wheelHintTitle: 'CAMBIO MANUALE',
  wheelHintBody: 'Una pressione sulla rotellina sinistra del volante. In che direzione lo decide l’auto, non tu:',
  wheelHintUp: 'In accelerazione · sale',
  wheelHintDown: 'In frenata · scende',
  wheelHintPaddles: 'Le palette a schermo fanno lo stesso.',
  wheelHintOk: 'CAPITO',
  wheelHintNever: 'Non mostrarlo più',
  termsLine: 'Continuando accetti i {terms} e la {privacy}.',
  termsWord: 'Termini',
  privacyWord: 'Informativa sulla privacy',
  accountCreated: 'Account creato',
  welcomeBack: 'Bentornato',
  followCreated: 'le tue auto e le impostazioni seguiranno questa email ovunque',
  followBack: 'le tue auto e le impostazioni sono tornate',
  scanPhone: 'Scansiona col telefono.',
  scanPhoneBody: 'Accedi lì e questo schermo accede da solo.',
  orTypeHere: 'oppure scrivila qui',
  emailIsAccount: 'La tua email {is} l’account, nessuna password. Ne hai già uno? Stessa casella. Prima volta? Il codice lo crea.',
  is: 'è',
  continueBtn: 'CONTINUA',
  sending: 'INVIO…',
  weEmailed: 'Abbiamo mandato un codice a {email}.',
  verify: 'VERIFICA',
  checking: 'CONTROLLO…',
  changeEmail: 'Cambia email',
};

const pt: Dict = {
  menu: 'Menu',
  account: 'Conta',
  signIn: 'Entrar',
  logOut: 'Sair',
  language: 'Idioma',
  languageAuto: 'Automático',
  languageAutoNote: 'Segue o teu telemóvel: {name}',
  admin: 'Admin',
  adminPanel: 'Painel de admin',
  diagnostics: 'Diagnóstico',
  change: 'mudar',

  tagline: 'Motores imersivos para o teu carro',
  nextEngine: 'Próximo motor',
  comingSoon: 'Em breve',
  comingSoonBody: 'Há mais motores no banco de ensaio. Entra e sê o primeiro a ouvi-los.',
  comingUp: 'A CAMINHO',
  free: 'GRÁTIS',
  owned: 'TEU',
  howItWorks: 'Como funciona',
  step1Title: 'Abre a web ou a app',
  step1Body: 'roardrive.com no navegador do carro, ou a app RoarDrive no telemóvel',
  step2Title: 'Escolhe um motor',
  step2Body: 'som real de carro de corrida',
  step3Title: 'Conduz',
  step3Body: 'a tua velocidade faz o resto',
  questions: 'Perguntas',
  q1: 'Funciona em qualquer carro?',
  a1: 'Sim. O GPS chega, o hardware é opcional.',
  q2: 'É uma subscrição?',
  a2: 'Não. Pagamento único por motor, com demo grátis antes.',
  q3: 'Ecrã do Tesla?',
  a3: 'Otimizado para ele: painel em ecrã inteiro que se atualiza sozinho.',
  q4: 'Distrai?',
  a4: 'Não se mexe em nada a conduzir. Configura-se parado e depois é só conduzir.',
  wallLine: 'Este mural está reservado a condutores a sério.',
  wallHow: 'Já experimentaste? O teu vai primeiro:',
  terms: 'Termos',
  privacy: 'Privacidade',
  cookies: 'Cookies',
  gdpr: 'Direitos RGPD',
  legalNotice: 'Aviso legal',
  drawerSignInLead: 'Um email, sem palavra-passe.',
  drawerSignInBody: 'As tuas compras e definições seguem-te em qualquer carro. Entra aqui, ou lê um QR com o telemóvel.',
  drawerAccountBody: 'Os teus motores e definições seguem este email em qualquer carro.',
  dangerZone: 'Zona de risco',
  deleteAccount: 'Eliminar conta',
  deleting: 'A eliminar…',
  deleteFailed: 'Não foi possível eliminar: este carro não está ligado a essa conta. Entra aqui primeiro, ou escreve para privacy@roardrive.com.',
  deleteConfirm: 'Eliminar a tua conta ROARDRIVE?\n\nSão apagados o email, os carros ligados, as definições e os tempos por volta. Os motores comprados são desligados e não podem passar para outra conta. Isto não tem volta.',
  deletedOk: 'Conta eliminada.',

  pitBox: 'Box',
  drive: 'CONDUZIR',
  drivingSource: 'Fonte de velocidade',
  drivingSourceWhy: 'O som segue a tua velocidade real. Quanto mais depressa a conseguirmos ler, mais colado fica ao teu pé direito. Escolhe todas as que tiveres: o motor usa sempre a melhor que estiver viva.',
  drivingOn: 'Agora com',
  proHw: 'Hardware Pro',
  instant: 'Instantâneo',
  proHwMeta: '25 Hz · sem atraso nenhum',
  proHwDesc: 'RaceBox ou OBD-II.',
  proHwDescDemo: 'RaceBox ou OBD-II. Vem com o motor.',
  fullEngine: 'Motor completo',
  teslaBrowser: 'Navegador do Tesla',
  fast: 'Rápido',
  teslaMeta: '10 Hz · o GPS do próprio carro',
  teslaDesc: 'Abre {site} no ecrã do próprio Tesla.',
  teslaFine: 'Abre-o no ecrã do Tesla e isto passa a ser o botão de reproduzir.',
  playOnScreen: '▶ Põe no ecrã do carro',
  sentToScreen: '✓ Enviado. Está a tocar no ecrã do carro.',
  commander: 'Enhauto Commander',
  experimental: 'Função experimental',
  experimentalSoon: 'Função experimental · em breve',
  commanderLead: 'Mais uma camada de imersão: o motor ouve o próprio carro. Passa o Tesla de {n} para {d} e mete 1ª, as {doors}, as {lights} e os {wipers} entram em jogo, e o {launch} arma-se sozinho quando carregas no travão a fundo durante 5 segundos.',
  doors: 'portas',
  lights: 'luzes',
  wipers: 'limpa-para-brisas',
  launchControl: 'launch control',
  cmStep1: 'App S3XY: Commander, {scan}. Uma vez e nunca mais.',
  cmStep2: 'Wi-Fi do telemóvel: liga-te a {ssid}, palavra-passe {pass}. O Wi-Fi em si, não a app S3XY: essa emparelha por Bluetooth e o RoarDrive lê o CAN por esta rede.',
  cmStep3: 'Definições do iPhone, RoarDrive, {local} ligado. Se recusares uma vez, o iOS nunca mais pergunta e bloqueia em silêncio.',
  cmNoReply: 'Ainda sem resposta.',
  cmNoReplyBody: 'Um dos três passos acima está por fazer.',
  waitingApp: 'À espera da app',
  connecting: 'A ligar…',
  linkedWaiting: 'Ligado, à espera do CAN',
  tapToConnect: 'Toca para ligar',
  optional: 'Opcional',
  answering: 'A responder',
  sim: 'SIM',
  simFree: 'Grátis',
  simMeta: 'sem carro · sem GPS',
  simDesc: 'Conduz o motor aqui mesmo: mantém premidos THROTTLE e BRAKE no ecrã.',
  simAdminName: 'SIM ADMIN',
  simAdminDesc: 'O simulador com o registo de sessão, os níveis de áudio e as etiquetas de telemetria.',
  phoneGps: 'GPS do telemóvel',
  backup: 'Reserva',
  delay: 'Atraso',
  phoneMeta: '{hz} · o GPS do próprio telemóvel',
  phoneDescBackup: 'Sempre ligado por baixo, como reserva. Caia o que cair, isto mantém o motor a trabalhar.',
  phoneDescAlone: 'Qualquer carro. Zero configuração.',
  detecting: 'a medir…',
  appRow: 'App RoarDrive no teu telemóvel',
  appRowBody: 'Abre-a, entra {who} e escolhe {pro}.',
  appRowWithEmail: 'como {email}',
  appRowNoEmail: 'com o teu email',
  appRowSignInHere: 'Ou {link} para os dois lados partilharem a conta.',
  appRowSignInLink: 'entra neste ecrã',
  linked: '✓ Ligada',
  obdRow: 'Adaptador OBD-II',
  obdStep1: 'Liga um adaptador ELM327 com Bluetooth LE à ficha OBD-II (normalmente por baixo do volante).',
  obdStep2: 'Ignição ligada.',
  obdStep3: 'É encontrado sozinho. Lê o velocímetro do próprio carro, por isso não há atraso de satélite nenhum.',
  obdBuyTitle: 'Que adaptador compro?',
  obdBuyBle: 'Bluetooth LE, também escrito BLE ou Bluetooth 4.0. Se a caixa diz apenas «Bluetooth», é o 3.0 antigo e o iPhone nem o vê.',
  obdBuyElm: 'Compatível com ELM327, que é o que quase todos são.',
  obdBuyWifi: 'Os de Wi-Fi não: ficam com a ligação do telemóvel e não os lemos.',
  obdBuyPrice: 'É uma peça de 25 a 45 euros. Pagar mais é pagar diagnóstico de oficina que não usamos.',
  obdBuyNames: 'Estes servem: Veepeak OBDCheck BLE+, Vgate iCar Pro BLE 4.0, vLinker MC+. Atenção ao sinal mais: o vLinker FD sem ele é Bluetooth 3.0 e nunca aparece num iPhone.',
  obdBuyEv: 'Carro elétrico? Muitos não publicam a velocidade na tomada padrão. Se o teu é elétrico, a compra segura é o RaceBox.',
  learnTitle: 'A tua caixa, medida',
  learnTitleSim: 'Medida no simulador',
  learnProgress: '{gears} mudanças · {min} min',
  learnHint: 'Liga um adaptador OBD-II e conduz como sempre. A app lê a tua velocidade e as tuas rotações e descobre sozinha a tua caixa: nada para preencher, nada para segurar, nenhuma mudança para esticar.',
  learnHintSim: 'Conduz uns dois minutos no simulador e isto enche-se com as mudanças do próprio motor. É a mesma medição que o adaptador OBD faz num carro a sério.',
  learnKeepGoing: 'Continua a conduzir: as mudanças do meio precisam de uns minutos de estrada normal.',
  learnReady: 'Medido a conduzires tu, até {rpm} rpm.',
  learnUse: 'Usar estas mudanças',
  learnForget: 'esquecer e começar de novo',
  learnFollowing: '● A seguir o teu carro: vais em {gear}ª e o motor também.',
  driveSafe: 'com segurança',
  audioDeniedTitle: 'O motor não conseguiu carregar',
  audioDeniedBody: 'As gravações não chegaram, por isso não toca nada. Costuma ser as audições gratuitas do dia esgotadas, ou uma ligação fraca. Entra com a tua conta se este motor é teu, ou tenta outra vez.',
  audioDeniedRetry: 'Tentar de novo',
  gpsDeniedTitle: 'A localização está bloqueada',
  gpsDeniedBody: 'O navegador do carro não entrega a sua posição, por isso o motor não tem nada para seguir. Normalmente a localização foi recusada uma vez para este site: no menu do navegador, limpe os dados de navegação, volte a abrir roardrive.com e aceite a localização quando for pedida. E lembre-se de que só ganha vida em andamento: parado, fica ao ralenti.',
  gpsDeniedRetry: 'Tentar de novo',
  yes: 'Sim',
  no: 'Não',
  learnFollowLabel: 'Mudar de mudança quando eu mudar',
  learnUseWhy: 'Guardá-las é para as viagens sem o adaptador: o motor muda onde o teu carro muda.',
  learnNotRacebox: 'Isto só um adaptador OBD-II consegue. O RaceBox dá posição e velocidade, não lê o motor, por isso não vê as tuas mudanças.',
  raceboxRow: 'RaceBox',
  rbStep1: 'Liga o RaceBox.',
  rbStep2: 'Fecha a app oficial do RaceBox (o Bluetooth só aceita uma ligação).',
  rbStep3: 'Liga-se sozinho{state}. Uma vez emparelhado, da próxima é automático.',
  stateSearching: ' (a procurar…)',
  stateFound: ' (encontrado, a ligar…)',
  stateSatellites: ' (à espera de satélites…)',
  live: '✓ Em direto',
  connectedIgnition: 'Ligado, ignição ligada?',
  connectedSatellites: 'Ligado · à espera de satélites…',
  searching: 'A procurar…',
  foundConnecting: 'Encontrado, a ligar…',
  fuseLive: 'Em direto · {hz} msg/s. RaceBox e OBD-II fundem-se, liga os dois ao mesmo tempo.',
  fuseFoot: 'Fundem-se. Liga vários ao mesmo tempo.',
  startingBridge: 'A arrancar a ponte de hardware… (Bluetooth ligado)',
  openBridge: 'Abrir a ponte (versão antiga da app)',
  findWiper: 'Encontra o bit dos limpa-para-brisas.',
  findWiperBody: 'Alterna, esperando cerca de 5 s de cada vez: limpa-para-brisas OFF toca {off}, ON toca {on}, depois off, depois on outra vez. Dois pares chegam.',
  off: 'off',
  on: 'on',
  reset: 'reiniciar',

  gearRatio: 'Relação de caixa',
  gearRatioWhy: 'O teu carro muda de mudança onde um carro de corrida não mudaria. Diz-nos qual é e o motor muda contigo.',
  offline: 'Sem cobertura',
  offlineWhy: 'O motor fica guardado neste dispositivo e anda só com o GPS: túneis, estradas de montanha, sem sinal nenhum. Descarrega-se sozinho.',
  offlineChecking: 'A verificar…',
  offlineDownloading: 'A descarregar o motor',
  offlineProgress: '{done} de {total} MB',
  offlineReady: 'Pronto sem cobertura',
  offlineReadyMeta: '{mb} MB neste dispositivo · só GPS',
  offlineFailed: 'A transferência não terminou',
  offlineFailedMeta: 'Ainda faltam alguns ficheiros. Tenta outra vez com melhor sinal.',
  offlineRetry: 'Tentar outra vez',
  offlineNoNet: 'Agora não há cobertura',
  offlineNoNetMeta: 'Descarrega-se sozinho quando voltares a ter ligação.',
  realistic: 'Realista',
  yourCarTab: 'O teu carro',
  realisticNote: 'Cada motor muda tal como o seu carro real: a caixa dele, conduzas o que conduzires.',
  topSpeedPerGear: 'velocidade máxima por mudança · {unit}',
  speedAtLimiter: 'velocidade no limitador por mudança · {unit}',
  evLadderNote: 'a caixa do motor, à escala · {unit}',
  ev: 'Elétrico',
  ice: 'Gasolina',
  topSpeed: 'Velocidade máxima',
  topSpeedOf: '{speed} de máxima',
  carLimit: '{speed} · limite do {name}',
  brandPick: 'Marca…',
  myOwnGears: 'As minhas mudanças',
  yourGears: 'As tuas mudanças',
  editedSuffix: '{model}, editado',
  backToCar: 'Voltar ao {model}',
  addGear: '+ mudança',
  removeGear: '− mudança',
  tapToChange: 'Toca numa velocidade para a mudar. Máximo por mudança = o do próprio motor: {limits} km/h',

  demoLine: 'Simulador grátis · na estrada {min} min hoje, limitador de box {cap} km/h · ',
  demoLineWeb: 'Na estrada {min} min hoje, limitador {cap} km/h · ',
  demoLineOpen: 'Simulador grátis · semana aberta: na estrada {min} min hoje, sem limitador · ',
  demoLineOpenWeb: 'Semana aberta: na estrada {min} min hoje, sem limitador · ',
  demoUsed: 'Simulador grátis · os minutos na estrada esgotaram-se hoje · ',
  demoBadge: 'DEMO · limitador {cap} km/h · mudança máx. {gear} · {time}',
  demoBadgeOpen: 'DEMO · {time}',
  demoBadgeBuy: 'DESBLOQUEAR',
  demoGearHeld: 'Demo: a caixa não passa da {gear}ª (limitador {cap} km/h). Desbloqueia este motor para teres todas as mudanças.',
  demoUsedWeb: 'Os minutos na estrada acabaram-se hoje · ',
  unlockFor: 'desbloquear por {price}',
  lastSession: 'ÚLTIMA SESSÃO',
  best: 'melhor',
  laps: '{n} voltas',
  lap1: '1 volta',
  teslaDetected: '🚗 Tesla detetado: {name}',
  recommendCarBrowser: 'Recomendamos conduzir no navegador do carro: abre lá {site}. Melhor GPS, e aqui aparece um botão {play}.',
  playThere: '«põe lá»',
  carScreenNotLinked: 'Ecrã do carro não ligado. Abre {site} no navegador do Tesla e aqui aparece sozinho um botão {play}.',

  tapToEnter: 'TOCA PARA ENTRAR',
  loading: 'A CARREGAR…',
  volumeNote: 'Na primeira vez, mantém o volume moderado',
  teslaAudioNote: 'Tesla: o áudio do navegador substitui a fonte que está a tocar',
  drivingInput: 'Fonte de velocidade',
  rotatePhone: 'Roda o telemóvel',
  hardwareGuide: 'Guia de configuração do hardware →',
  units: 'Unidades',
  metric: 'Métrico',
  imperial: 'Imperial',
  demoOver: 'DEMO TERMINADA',
  unlockFull: 'Motor completo · {price} pagamento único',
  payOnPhone: 'Pagamento seguro com Stripe · o motor desbloqueia neste ecrã assim que pagares',
  backToEngines: '← Voltar aos motores',

  demoFineLeft: 'Simulador grátis, sem relógio · na estrada {min} min hoje, limitador {cap} km/h',
  demoFineLeftWeb: 'Na estrada {min} min hoje, limitador {cap} km/h',
  demoFineLeftOpen: 'Simulador grátis, sem relógio · semana aberta: na estrada {min} min hoje, sem limitador',
  demoFineLeftOpenWeb: 'Semana aberta: na estrada {min} min hoje, sem limitador',
  demoFineUsed: 'Simulador grátis, sem relógio · os {min} min na estrada esgotaram-se hoje',
  demoFineUsedWeb: 'Os {min} min na estrada acabaram-se hoje',
  buyInApp: 'Comprar · {price}',
  buying: 'A abrir a App Store…',
  buyCancelled: 'Compra cancelada.',
  buyDone: 'Desbloqueado. Aproveita.',
  purchases: 'Compras',
  restorePurchases: 'Restaurar compras',
  restoreNote: 'Mudaste de telemóvel ou reinstalaste a app? Os teus motores voltam aqui. Não se cobra nada outra vez: no iPhone pergunta-se à Apple o que é teu, e em tudo o resto olha-se para a tua conta.',
  restoring: 'A restaurar…',
  restoreDone: '✓ A tua compra voltou. Aproveita.',
  restoreNone: 'Não há nada de novo para restaurar neste dispositivo. Se pagaste com outro email, entra com esse e o motor segue-te.',
  restoreFailed: 'Não foi possível restaurar agora. Tenta novamente daqui a pouco.',
  buyFailed: 'A compra não foi concluída. Não foi cobrado nada.',
  pqrScan: 'Lê com o telemóvel para pagar · desbloqueia sozinho',
  pqrPreparing: 'A preparar o pagamento seguro…',
  pqrUnlocked: '✓ Desbloqueado',
  pqrNotConfigured: 'Os pagamentos ainda não estão configurados.',
  pqrError: 'O pagamento não está disponível agora. Tenta mais tarde.',
  buyNeedAccount: 'Primeiro, a tua conta',
  buyNeedAccountBody: 'O motor fica guardado na conta, não neste ecrã. Entra com o teu email e recuperas o motor no carro, no telemóvel e depois de qualquer reinstalação. Um código, sem palavra-passe.',
  buyNeedAccountBtn: 'CRIAR CONTA · ENTRAR',
  payNow: 'PAGAR {price}',
  pqrPayNote: 'Pagamento seguro com Stripe · o recibo vai para {email}',
  storeNotAnswering: 'A App Store não responde. Tenta daqui a um momento.',
  buyingPlay: 'A abrir o Google Play…',
  storeNotAnsweringPlay: 'O Google Play não responde. Tenta daqui a um momento.',
  notInAppYet: 'Em breve na app',
  notInAppYetFine: 'Este motor ainda não está à venda na app. A demo já é tua.',
  linkTitle: 'Entra no teu Tesla',
  linkDone: '✓ Pronto',
  linkDoneBody: 'O teu Tesla entrou como {email}. Olha para o ecrã do carro. Já podes fechar isto.',
  linkHint: 'Só o teu email, sem palavra-passe. Primeira vez? Isso cria a tua conta.',
  signInBtn: 'ENTRAR',
  errSendCode: 'Não conseguimos enviar o código. Verifica o email e tenta outra vez.',
  errBadCode: 'Esse código não está certo.',
  engineStopHint: 'Pára o carro para desligar o motor',
  demoWord: 'DEMO',
  demoSub: 'grátis',
  demoFine: 'O motor completo, limitado a {cap} km/h · uns minutos por dia',
  purchase: 'COMPRAR',
  purchaseFine: 'Pagamento único · o motor completo, sem limites · teu em qualquer carro',
  oneTime: '{price} · pagamento único',
  savedToAccount: '✓ {email} · o carro fica guardado nesta conta',
  unlockNote: 'Desbloqueia aqui assim que pagares. O motor pertence à tua conta: entra com o mesmo email em qualquer carro ou telemóvel e está lá.',

  signInSignUp: 'ENTRAR · CRIAR CONTA',
  notifyMe: 'Avisa-me quando sair um motor novo',
  wheelHintTitle: 'CAIXA MANUAL',
  wheelHintBody: 'Uma pressão na roda esquerda do volante. Para onde vai decide o carro, não tu:',
  wheelHintUp: 'A acelerar · sobe',
  wheelHintDown: 'A travar · desce',
  wheelHintPaddles: 'As patilhas do ecrã fazem o mesmo.',
  wheelHintOk: 'PERCEBIDO',
  wheelHintNever: 'Não mostrar outra vez',
  termsLine: 'Ao continuar aceitas os {terms} e a {privacy}.',
  termsWord: 'Termos',
  privacyWord: 'Política de Privacidade',
  accountCreated: 'Conta criada',
  welcomeBack: 'Bem-vindo de volta',
  followCreated: 'os teus carros e definições vão seguir este email para todo o lado',
  followBack: 'os teus carros e definições estão de volta',
  scanPhone: 'Lê com o telemóvel.',
  scanPhoneBody: 'Entra lá e este ecrã entra sozinho.',
  orTypeHere: 'ou escreve-o aqui',
  emailIsAccount: 'O teu email {is} a conta, sem palavra-passe. Já tens uma? A mesma caixa. Primeira vez? O código cria-a.',
  is: 'é',
  continueBtn: 'CONTINUAR',
  sending: 'A ENVIAR…',
  weEmailed: 'Enviámos um código para {email}.',
  verify: 'VERIFICAR',
  checking: 'A VERIFICAR…',
  changeEmail: 'Mudar de email',
};

export const STRINGS: Record<Lang, Dict> = { en, es, ca, fr, de, it, pt };
